import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Space,
  Typography,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Tooltip,
  Alert,
  Modal,
  Checkbox,
  Switch,
  Tag,
  Table,
  message,
  Spin,
  Divider,
  Tabs,
} from 'antd';
import {
  DeleteOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
  GiftOutlined,
  PlusOutlined,
  ClearOutlined,
  PictureOutlined,
  TagsOutlined,
  AimOutlined,
  TeamOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import CampaignRequestorAdGroupsDialog from '../CampaignRequestorAdGroupsDialog';
import CampaignRequestorRegionGroupsDialog from '../CampaignRequestorRegionGroupsDialog';
import CampaignRequestorTargetingGroupsDialog from '../CampaignRequestorTargetingGroupsDialog';
import {
  validateCampaignDates,
  validateLineItemBudgets,
  detectFlightOverlaps,
} from '../utils/campaignValidation';
import { clampCampaignToEndDate } from '../utils/cascadeDates';
import { applyRenewalToCampaign } from '../utils/renewalFlights';
import {
  countries,
  iabCategories,
  regulatedCategories,
} from '../../../../core/utils/constants/constants';
import { usePermify } from '@permify/react-role';
import { useApolloClient } from 'react-apollo';
import GET_NEXXEN_PUBLISHER_DEALS from '../../../../core/GraphQl/Queries/GET_NEXXEN_PUBLISHER_DEALS';
import MediaPlanWidget from './MediaPlanWidget';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import {
  getCpmForCampaignType,
  isPercentageModel,
  getMinimumCpmForCampaignType,
  getPercentageForAdvertiser,
  getMinimumPercentageForAdvertiser,
  validatePercentageOverride,
} from '../../../../core/utils/pricing/cpmHelper';
import { getPricingFieldsForSave } from '../../../../core/utils/pricing/pricingFieldMapper';
import { computeCampaignImpressions } from '../../../../core/utils/campaigns';
import moment from 'moment-timezone';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { deriveTrendsGeo } from '../../../../core/utils/geo/trendsGeo';
import { determineUserAccess } from '../../../../core/permissions/userLevelAccess';

// An IAB code is a brand-safety classification, not something anyone searches.
// These are the buying-intent phrases for each regulated category.
const RESEARCH_TERMS = {
  'IAB7-5': 'cannabis dispensary',
  'IAB9-30': 'liquor store',
  'IAB1-4': 'online casino',
};

const { Text } = Typography;
const { confirm } = Modal;

// Wrapper that converts Form.Item's injected UTC value to user timezone for display
const TimezoneDatePicker = ({ value, onChange, timezone, ...props }) => {
  const displayValue = value ? moment(value).tz(timezone) : null;
  return <DatePicker {...props} value={displayValue} onChange={onChange} />;
};

/**
 * CampaignHeader Component
 *
 * Displays and manages campaign-level configuration within a campaign order.
 * Each campaign has:
 * - Type (Display, Video, External) - required
 * - Name (required)
 * - Budget allocation (required)
 * - Optional date range override (defaults to order dates)
 * - Clone action (creates a duplicate campaign)
 * - Delete action (disabled if only 1 campaign exists)
 *
 * Props:
 * @param {number} campaignIndex - Index of the campaign in the campaigns array
 * @param {Object} form - Ant Design form instance
 * @param {number} orderBudget - Total order budget for validation
 * @param {Object} orderDates - { startDate, endDate } from order level
 * @param {Function} onClone - Callback when clone button clicked
 * @param {Function} onDelete - Callback when delete button clicked
 * @param {number} campaignCount - Total number of campaigns (for delete constraint)
 * @param {Array} campaigns - All campaigns for budget calculations
 * @param {boolean} isAdvancedMode - Whether advanced mode is enabled
 * @param {Object} helpers - Helper functions from useCampaignForm (includes isCloning)
 */
const CampaignHeader = React.memo(
  ({
    campaignIndex,
    form,
    orderBudget,
    orderDates,
    onClone,
    onDelete,
    campaignCount,
    campaigns = [],
    isAdvancedMode = false,
    helpers,
    hasTraffickerOrProcessorPermission = false,
    isNotDraftStatus = false,
  }) => {
    // Get selected advertiser and campaign order from context for pricing model
    const { selectedAdvertiser, currentCampaignOrder, childrenOrg, currentUser } =
      useCampaignRequestorContext();

    // Behind the beta flag while the plan generator is still being validated.
    const canSeeMediaPlan = determineUserAccess(currentUser?.permission).isBeta;
    const { timezone } = useTimezone();
    const apolloClient = useApolloClient();

    // Get current campaign data from form
    const campaignType = Form.useWatch(['campaigns', campaignIndex, 'type'], form);
    const campaignName = Form.useWatch(['campaigns', campaignIndex, 'name'], form);
    const campaignBudget = Form.useWatch(['campaigns', campaignIndex, 'budget'], form) || 0;
    const campaignCpm = Form.useWatch(['campaigns', campaignIndex, 'cpm'], form);
    const campaignStartDate = Form.useWatch(['campaigns', campaignIndex, 'startDate'], form);
    const campaignEndDate = Form.useWatch(['campaigns', campaignIndex, 'endDate'], form);
    const lineItems = Form.useWatch(['campaigns', campaignIndex, 'lineItems'], form) || [];
    const hasMediaPlanStrategy = Form.useWatch(
      ['campaigns', campaignIndex, 'hasMediaPlanStrategy'],
      form
    );
    const isLaunchProgrammatically = Form.useWatch(
      ['campaigns', campaignIndex, 'isLaunchProgrammatically'],
      form
    );

    // Read off the order. Deliberately ignores `awarenessCampaign` — unreliable on
    // real data, like transactionTarget and attributionTargets.
    const planObjective = useMemo(() => {
      if (currentCampaignOrder?.measureWalkInTraffic) return 'traffic';
      if (currentCampaignOrder?.transactionsConfig) return 'conversions';
      if (currentCampaignOrder?.kpi === 'CPA') return 'conversions';
      if (currentCampaignOrder?.kpi === 'CPC') return 'traffic';
      if (currentCampaignOrder?.kpi === 'CPM') return 'awareness';
      return 'traffic';
    }, [currentCampaignOrder]);

    // IAB7-5 is "Alternative Medicine" in the general taxonomy but Cannabis / CBD
    // in the regulated one, so the regulated list wins. Overridable in the widget.
    const orderCategoryCode = Form.useWatch('category', form);
    const researchCategory = useMemo(() => {
      const code = Array.isArray(orderCategoryCode) ? orderCategoryCode[0] : orderCategoryCode;
      if (!code) return null;
      if (RESEARCH_TERMS[code]) return RESEARCH_TERMS[code];
      const match =
        regulatedCategories.find((iab) => iab.code === code) ||
        iabCategories.find((iab) => iab.code === code);
      return match ? match.category : null;
    }, [orderCategoryCode]);

    // From the form, not `selectedAdvertiser`: antd fires onValuesChange on user
    // interaction only, so in edit mode selectedAdvertiser is still the current
    // user's org until someone touches the dropdown.
    const formAdvertiserId = Form.useWatch('advertiserId', form);
    const advertiserOrg = useMemo(() => {
      const fromList = (childrenOrg || []).find((org) => org.id === formAdvertiserId);
      return fromList || selectedAdvertiser || null;
    }, [childrenOrg, formAdvertiserId, selectedAdvertiser]);

    // Point the research at the same map the campaign buys against.
    const campaignLineItems = Form.useWatch(['campaigns', campaignIndex, 'lineItems'], form);
    const orderAttributionTargets = Form.useWatch('attributionTargets', form);
    const { geo: researchGeo, city: researchCity } = useMemo(
      () =>
        deriveTrendsGeo({
          lineItems: campaignLineItems || [],
          attributionTargets: orderAttributionTargets || [],
        }),
      [campaignLineItems, orderAttributionTargets]
    );

    // Flight schedule state for campaign-level flights
    const campaign = campaigns[campaignIndex] || {};

    // Get campaign status from the current campaign in the form
    const campaignStatus = campaign?.status;
    const campaignValueAdd = Form.useWatch(['campaigns', campaignIndex, 'valueAdd'], form);
    const campaignDsp = Form.useWatch(['campaigns', campaignIndex, 'demandSidePlatform'], form);
    const [flightSchedule, setFlightSchedule] = useState(
      campaign.flightSchedules || campaign.flightSchedule || []
    );
    const [hasInitializedFlightSchedule, setHasInitializedFlightSchedule] = useState(false);

    // Campaign Deal IDs state for Advanced Mode
    const [campaignDealIds, setCampaignDealIds] = useState('');
    const [loadingDealIds, setLoadingDealIds] = useState(false);
    const [publisherDeals, setPublisherDeals] = useState([]);
    const [dealsFetchProgress, setDealsFetchProgress] = useState('');

    // Campaign Creatives state for applying ad groups to line items
    const [showCampaignAdGroupsModal, setShowCampaignAdGroupsModal] = useState(false);
    const [selectedAdGroups, setSelectedAdGroups] = useState([]); // Ad groups selected from library
    const [adGroupClickthroughs, setAdGroupClickthroughs] = useState({}); // { adGroupName: clickthroughUrl }
    const [selectedLineItemIndices, setSelectedLineItemIndices] = useState([]); // Line items to apply to

    // Bulk Targeting Type state
    const [bulkTargetingType, setBulkTargetingType] = useState(null);
    const [targetingLineItemIndices, setTargetingLineItemIndices] = useState([]);
    const [bulkTargetingCountry, setBulkTargetingCountry] = useState(null);
    const [showBulkRegionGroupsModal, setShowBulkRegionGroupsModal] = useState(false);
    const [selectedBulkRegionGroups, setSelectedBulkRegionGroups] = useState([]);
    const [showBulkTargetingGroupsModal, setShowBulkTargetingGroupsModal] = useState(false);
    const [selectedBulkTargetingGroups, setSelectedBulkTargetingGroups] = useState([]);

    // Bulk Audience Type state
    const [bulkAudienceType, setBulkAudienceType] = useState(null);
    const [audienceLineItemIndices, setAudienceLineItemIndices] = useState([]);

    // URL validation helper
    const isValidUrl = useCallback((url) => {
      if (!url) return true; // Empty is valid (optional field)
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }, []);

    // Clean up line item indices when lineItems change (prevents stale indices)
    useEffect(() => {
      setSelectedLineItemIndices((prev) => prev.filter((idx) => idx < lineItems.length));
      setTargetingLineItemIndices((prev) => prev.filter((idx) => idx < lineItems.length));
      setAudienceLineItemIndices((prev) => prev.filter((idx) => idx < lineItems.length));
    }, [lineItems.length]);

    // Auto-select all line items when bulk targeting type is selected
    useEffect(() => {
      if (bulkTargetingType && lineItems.length > 0) {
        setTargetingLineItemIndices(lineItems.map((_, idx) => idx));
      }
    }, [bulkTargetingType, lineItems, lineItems.length]);

    // Auto-select all line items when bulk audience type is selected
    useEffect(() => {
      if (bulkAudienceType && lineItems.length > 0) {
        setAudienceLineItemIndices(lineItems.map((_, idx) => idx));
      }
    }, [bulkAudienceType, lineItems, lineItems.length]);

    // Refs for async safety and fetch tracking
    const isMountedRef = React.useRef(true);
    const hasFetchedDealsRef = React.useRef(false);

    // Set mounted ref on mount, clear on unmount
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Initialize flight schedule from existing campaign data ONCE only
    useEffect(() => {
      const existingFlights = campaign.flightSchedules || campaign.flightSchedule || [];
      if (existingFlights.length > 0 && !hasInitializedFlightSchedule) {
        setFlightSchedule(existingFlights);
        setHasInitializedFlightSchedule(true);
      }
    }, [campaign.flightSchedules, campaign.flightSchedule, hasInitializedFlightSchedule]);

    // Fetch publisher deals from Nexxen (reused from CampaignRequestorInventoryDialog)
    const fetchPublisherDeals = useCallback(async () => {
      if (!isMountedRef.current) return;

      setLoadingDealIds(true);
      setDealsFetchProgress('Loading publisher deals...');
      const allDeals = [];
      let offset = 0;
      const limit = 100;

      try {
        while (isMountedRef.current) {
          const { data } = await apolloClient.query({
            query: GET_NEXXEN_PUBLISHER_DEALS,
            variables: { forceRefresh: false, offset, limit },
            fetchPolicy: 'cache-first',
          });

          if (!isMountedRef.current) return;

          const pageData = data?.nexxenPublisherDeals?.data || [];
          const total = data?.nexxenPublisherDeals?.queryTotal || 0;

          // Use push for better performance (O(n) vs O(n²) with spread)
          allDeals.push(...pageData);

          if (isMountedRef.current) {
            setDealsFetchProgress(`Loaded ${allDeals.length} of ${total} publisher deals...`);
          }

          if (allDeals.length >= total || pageData.length === 0) break;
          offset += limit;
        }

        if (isMountedRef.current) {
          // Deduplicate deals by dealId
          const uniqueDeals = Array.from(
            new Map(allDeals.map((deal) => [deal.dealId, deal])).values()
          );
          setPublisherDeals(uniqueDeals);
          hasFetchedDealsRef.current = true;
        }
      } catch (error) {
        console.error('Error fetching publisher deals:', error);
        if (isMountedRef.current) {
          // Graceful degradation: don't let Nexxen API failures crash the page
          // or trigger logout. The edit screen works without live deal data —
          // deal ID validation just won't have the latest publisher deal list.
          const isAuthError =
            error?.networkError?.statusCode === 401 ||
            error?.graphQLErrors?.some(
              (e) => e?.message?.includes('Unauthorized') || e?.message?.includes('401')
            ) ||
            error?.message?.includes('Failed to fetch');
          if (isAuthError) {
            message.warning(
              'Nexxen API unavailable — deal ID validation disabled for this session.'
            );
          } else {
            message.warning('Could not load publisher deals. Deal ID validation is unavailable.');
          }
          setPublisherDeals([]);
          // Don't clear campaignDealIds — the user may have manually entered valid IDs
          hasFetchedDealsRef.current = true; // Mark as fetched to prevent retry loops
        }
      } finally {
        if (isMountedRef.current) {
          setLoadingDealIds(false);
          setDealsFetchProgress('');
        }
      }
    }, [apolloClient]);

    // Trigger fetch when NEXXEN DSP is selected
    useEffect(() => {
      if (campaignDsp === 'NEXXEN' && !hasFetchedDealsRef.current && !loadingDealIds) {
        fetchPublisherDeals();
      } else if (campaignDsp !== 'NEXXEN') {
        // Reset state when DSP changes away from NEXXEN
        setPublisherDeals([]);
        setCampaignDealIds('');
        setLoadingDealIds(false);
        hasFetchedDealsRef.current = false;
      }
    }, [campaignDsp, fetchPublisherDeals, loadingDealIds]);

    // Sync flight schedule state to form values whenever it changes.
    // lastPushedFlightsRef tracks what local state last wrote to the form: when
    // the form's flights change WITHOUT a local edit (order-level Renew, date
    // cascade), the external value is ADOPTED into local state instead of being
    // clobbered by the stale local copy.
    const lastPushedFlightsRef = React.useRef(flightSchedule);
    useEffect(() => {
      const currentFormFlightSchedule =
        form.getFieldValue(['campaigns', campaignIndex, 'flightSchedule']) || [];

      // Simple length check + JSON comparison to detect changes
      const hasChanged =
        JSON.stringify(currentFormFlightSchedule) !== JSON.stringify(flightSchedule);

      if (!hasChanged) {
        lastPushedFlightsRef.current = flightSchedule;
        return;
      }

      const localDirty =
        JSON.stringify(lastPushedFlightsRef.current) !== JSON.stringify(flightSchedule);

      if (!localDirty) {
        // External change (e.g. Renew, cascade) — adopt it into local state
        lastPushedFlightsRef.current = currentFormFlightSchedule;
        setFlightSchedule(currentFormFlightSchedule);
        return;
      }

      const fullFormValues = form.getFieldsValue();
      const allCampaigns = fullFormValues.campaigns || [];

      const updatedAllCampaigns = allCampaigns.map((c, cIdx) => {
        if (cIdx === campaignIndex) {
          return {
            ...c,
            flightSchedule: flightSchedule,
          };
        }
        return c;
      });

      lastPushedFlightsRef.current = flightSchedule;
      form.setFieldsValue({ campaigns: updatedAllCampaigns });

      // Trigger form validation to update validationState immediately
      form.validateFields().catch(() => {});
    }, [flightSchedule, form, campaignIndex, campaigns]);

    /**
     * Auto-adjust campaign order end date when flight schedule end date exceeds it
     */
    const autoAdjustEndDatesFromFlightSchedule = (updatedFlightSchedule) => {
      if (!updatedFlightSchedule || updatedFlightSchedule.length === 0) return;

      // Find the furthest end date among all flight schedules
      let furthestEndDate = null;
      updatedFlightSchedule.forEach((flight) => {
        if (flight.endDate) {
          const flightEnd = moment(flight.endDate);
          if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
            furthestEndDate = flightEnd;
          }
        }
      });

      if (!furthestEndDate) return;

      const fullFormValues = form.getFieldsValue();
      const fullFormUpdates = {};

      // Check if campaign order end date needs adjustment
      const currentOrderEnd = fullFormValues.endDate ? moment(fullFormValues.endDate) : null;
      let needsOrderUpdate = false;
      if (!currentOrderEnd || furthestEndDate.isAfter(currentOrderEnd)) {
        needsOrderUpdate = true;
        fullFormUpdates.endDate = furthestEndDate;
      }

      // Check if campaign end date needs adjustment
      const allCampaigns = fullFormValues.campaigns || [];
      const currentCampaign = allCampaigns[campaignIndex];
      const currentCampaignEnd = currentCampaign?.endDate ? moment(currentCampaign.endDate) : null;
      let needsCampaignUpdate = false;
      if (!currentCampaignEnd || furthestEndDate.isAfter(currentCampaignEnd)) {
        needsCampaignUpdate = true;
        const updatedAllCampaigns = allCampaigns.map((c, cIdx) => {
          if (cIdx === campaignIndex) {
            return { ...c, endDate: furthestEndDate };
          }
          return c;
        });
        fullFormUpdates.campaigns = updatedAllCampaigns;
      }

      if (Object.keys(fullFormUpdates).length > 0) {
        form.setFieldsValue(fullFormUpdates);
        // Sync antd form values back into the controller's formValues state so
        // validations (which read formValues) see the extended order/campaign
        // end dates instead of a stale value.
        helpers.syncFormValues();

        const adjustedItems = [];
        if (needsCampaignUpdate) adjustedItems.push('campaign');
        if (needsOrderUpdate) adjustedItems.push('campaign order');

        message.info(
          `End date auto-adjusted for ${adjustedItems.join(', ')} to match flight schedule end date (${furthestEndDate.format('MM/DD/YYYY')})`,
          4
        );
      }
    };

    // ─── Renew ──────────────────────────────────────────────────────────
    // Appends monthly renewal flights to this campaign and its line items
    // (pro-rata by budget share), then extends the order totals. Order-level
    // renewal lives in CampaignRequestorBudget. See utils/renewalFlights.
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [renewMonths, setRenewMonths] = useState(1);
    const [renewMonthlyBudget, setRenewMonthlyBudget] = useState(null);
    const [renewEffectiveDate, setRenewEffectiveDate] = useState(null);

    const renewalOptions = {
      newMonthlyBudget: renewMonthlyBudget,
      effectiveDate: renewEffectiveDate,
    };

    const renewalPreview = showRenewModal
      ? applyRenewalToCampaign(campaign, renewMonths, timezone, renewalOptions)
      : null;

    const handleRenewConfirm = () => {
      const allCampaigns = form.getFieldValue('campaigns') || [];
      const currentCampaign = allCampaigns[campaignIndex];
      const result = applyRenewalToCampaign(currentCampaign, renewMonths, timezone, renewalOptions);

      if (!result) {
        message.info('No existing flight schedule to renew from');
        setShowRenewModal(false);
        return;
      }

      const updatedCampaigns = allCampaigns.map((c, cIdx) =>
        cIdx === campaignIndex ? result.campaign : c
      );
      const currentOrderBudget = Number(form.getFieldValue('budget')) || 0;

      form.setFieldsValue({
        campaigns: updatedCampaigns,
        budget: Number((currentOrderBudget + result.renewalTotal).toFixed(2)),
      });
      helpers.syncFormValues();

      // Extend campaign/order end dates to cover the renewal (no-op if covered)
      autoAdjustEndDatesFromFlightSchedule(result.allFlights);

      setShowRenewModal(false);
      setRenewMonths(1);
      setRenewMonthlyBudget(null);
      setRenewEffectiveDate(null);
      message.success(
        `Renewed campaign for ${renewMonths} month(s) — added $${result.renewalTotal.toFixed(2)}`
      );
    };

    // Check if pricing model is Percentage (hide CPM fields)
    // For existing campaigns, use the saved pricingModel; for new campaigns, use advertiser's pricing model
    const savedPricingModel = currentCampaignOrder?.pricingModel;
    const hideCpmFields = useMemo(() => {
      if (savedPricingModel) {
        return savedPricingModel === 'Percentage';
      }
      return isPercentageModel(selectedAdvertiser);
    }, [savedPricingModel, selectedAdvertiser]);

    // Determine pricing model for display in Advanced Mode
    const pricingModel = useMemo(() => {
      // Priority 1: Use pricingModel directly from campaignOrder (string value)
      let model = currentCampaignOrder?.pricingModel;

      // Priority 2: Try selectedAdvertiser.pricingModel.model
      if (!model && selectedAdvertiser?.pricingModel?.model) {
        model = selectedAdvertiser.pricingModel.model;
      }

      // Priority 3: Try currentCampaignOrder.advertiser.pricingModel.model
      if (!model && currentCampaignOrder?.advertiser?.pricingModel?.model) {
        model = currentCampaignOrder.advertiser.pricingModel.model;
      }

      if (!model) {
        return null;
      }

      if (model === 'Percentage') {
        return {
          name: 'Percentage',
          description: 'Budget-based pricing',
          color: 'purple',
          tooltip:
            'This campaign uses percentage-based pricing where costs are calculated as a percentage of the budget allocation.',
        };
      }

      if (model === 'CPM') {
        return {
          name: 'CPM',
          description: 'Impression-based pricing',
          color: 'blue',
          tooltip:
            'This campaign uses CPM (Cost Per Mille) pricing where costs are based on impressions delivered.',
        };
      }

      return null;
    }, [selectedAdvertiser, currentCampaignOrder]);

    // Get campaign order name for checkbox functionality
    const campaignOrderName = Form.useWatch('name', form);

    // Track previous campaign type to detect changes
    const [previousType, setPreviousType] = React.useState(campaignType);

    // Track if CPM has been initialized to prevent auto-fill when user clears field
    const cpmInitialized = React.useRef(false);

    // State for "Same as campaign order name" checkbox. Derived from the data
    // instead of always-on: a campaign with its own stored name opens UNCHECKED
    // (input enabled, name preserved — previously opening the editor clobbered
    // custom names back to the order name); a nameless campaign syncs by default
    // only for the FIRST campaign — additional sub-campaigns get their own names
    // by default since they usually carry different tactics.
    const initialUseOrderName = () => {
      const name = campaign.name;
      if (!name) return campaignIndex === 0;
      const orderName = form.getFieldValue('name');
      if (!orderName) return campaignIndex === 0;
      return name === orderName || name === `${orderName}-$`;
    };
    const [useCampaignOrderName, setUseCampaignOrderName] = useState(initialUseOrderName);
    // Re-derive once the form data arrives (campaigns/order name populate after
    // mount for existing orders). One-shot — never fights an explicit user choice.
    const hasDerivedNameSyncRef = React.useRef(false);
    useEffect(() => {
      if (hasDerivedNameSyncRef.current) return;
      const name = form.getFieldValue(['campaigns', campaignIndex, 'name']);
      const orderName = form.getFieldValue('name');
      if (!name || !orderName) return; // data not loaded yet
      hasDerivedNameSyncRef.current = true;
      setUseCampaignOrderName(name === orderName || name === `${orderName}-$`);
    }, [campaign, campaignIndex, form]);
    const prevOrderNameRef = React.useRef(null);

    // Permission checking for budget warning visibility
    const { isAuthorized } = usePermify();
    const [showBudgetWarning, setShowBudgetWarning] = useState(false);
    const [canEditCpm, setCanEditCpm] = useState(false);

    // Check if user is Ad Ops (only Ad Ops should see budget warning)
    useEffect(() => {
      const checkPermissions = async () => {
        const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);
        setShowBudgetWarning(hasAdOpsPermission);
      };

      checkPermissions();
    }, [isAuthorized]);

    // Check if user can edit CPM fields (only Ad Ops)
    useEffect(() => {
      const checkCpmPermissions = async () => {
        const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);
        setCanEditCpm(hasAdOpsPermission);
      };
      checkCpmPermissions();
    }, [isAuthorized]);

    // Auto-populate CPM from advertiser when creating a new campaign order
    useEffect(() => {
      // Only auto-populate if:
      // 1. We have a selected advertiser and campaign type
      // 2. Current campaign order is not set (new campaign order)
      // 3. CPM has not been initialized yet (first time only)
      if (!selectedAdvertiser || !campaignType || currentCampaignOrder?.id) {
        return;
      }

      // Skip if we've already initialized CPM - don't auto-fill when user clears
      if (cpmInitialized.current) {
        return;
      }

      if (campaignCpm && campaignCpm > 0) {
        // CPM already has a value, mark as initialized
        cpmInitialized.current = true;
        return;
      }

      // Get CPM from advertiser based on campaign type
      const advertiserCpm = getCpmForCampaignType(selectedAdvertiser, campaignType);
      const pricingFields = getPricingFieldsForSave(
        advertiserCpm,
        selectedAdvertiser,
        campaignType
      );

      // Set the CPM value in the form
      if (pricingFields.cpm !== campaignCpm) {
        form.setFieldValue(['campaigns', campaignIndex, 'cpm'], pricingFields.cpm);

        // Also set contractCPM if it exists
        if (pricingFields.contractCPM) {
          form.setFieldValue(
            ['campaigns', campaignIndex, 'contractCPM'],
            pricingFields.contractCPM
          );
        }

        // Set percentage fields if applicable
        if (pricingFields.advertiserPercentage !== null) {
          form.setFieldValue(
            ['campaigns', campaignIndex, 'advertiserPercentage'],
            pricingFields.advertiserPercentage
          );
        }
        if (pricingFields.contractPercentage !== null) {
          form.setFieldValue(
            ['campaigns', campaignIndex, 'contractPercentage'],
            pricingFields.contractPercentage
          );
        }

        // Mark as initialized after setting
        cpmInitialized.current = true;
      }
    }, [
      selectedAdvertiser,
      campaignType,
      campaignCpm,
      currentCampaignOrder,
      form,
      campaignIndex,
      hideCpmFields,
    ]);

    // NOTE: Percentage auto-population is now handled in CampaignRequestorEdit.js
    // when the advertiser changes, along with CPM updates. This ensures consistent
    // batch updates for all pricing fields (CPM, contractCPM, advertiserPercentage, contractPercentage)

    // Auto-update campaign name when campaign order name changes (if checkbox is checked)
    // Debounced to avoid updating on every keystroke
    useEffect(() => {
      if (!useCampaignOrderName || !campaignOrderName) {
        return;
      }

      // Debounce the update by 500ms
      const timeoutId = setTimeout(() => {
        const currentValueAdd = form.getFieldValue(['campaigns', campaignIndex, 'valueAdd']);
        const nameWithSuffix =
          currentValueAdd && !campaignOrderName.endsWith('-$')
            ? `${campaignOrderName}-$`
            : campaignOrderName;

        // Never clobber a stored campaign name that doesn't already track the
        // order name (e.g. loading an order whose sub-campaigns have their own
        // names). Sync only when the campaign name is empty (fresh campaign) or
        // matches the PREVIOUS order name (genuine order rename).
        const currentName = form.getFieldValue(['campaigns', campaignIndex, 'name']) || '';
        const prevOrderName = prevOrderNameRef.current;
        prevOrderNameRef.current = campaignOrderName;
        const prevWithSuffix =
          prevOrderName && currentValueAdd && !prevOrderName.endsWith('-$')
            ? `${prevOrderName}-$`
            : prevOrderName;
        const tracksOrderName =
          currentName === '' ||
          (prevOrderName != null &&
            (currentName === prevOrderName || currentName === prevWithSuffix));
        if (!tracksOrderName) return;

        form.setFieldValue(['campaigns', campaignIndex, 'name'], nameWithSuffix);
        // Trigger validation to clear any errors
        setTimeout(() => {
          form.validateFields([['campaigns', campaignIndex, 'name']]).catch(() => {
            // Ignore validation errors
          });
        }, 0);
      }, 500);

      // Cleanup: cancel the timeout if campaignOrderName changes again before 500ms
      return () => clearTimeout(timeoutId);
    }, [campaignOrderName, useCampaignOrderName, form, campaignIndex]);

    // Calculate validation states
    const dateValidation = useMemo(() => {
      if (!orderDates.startDate || !orderDates.endDate) {
        return { valid: true, error: null };
      }

      return validateCampaignDates(
        {
          startDate: campaignStartDate,
          endDate: campaignEndDate,
        },
        orderDates.startDate,
        orderDates.endDate
      );
    }, [campaignStartDate, campaignEndDate, orderDates]);

    const budgetValidation = useMemo(() => {
      // Check if at least one line item exists (regardless of delivery status)
      if (!lineItems || lineItems.length === 0) {
        return {
          valid: false,
          error: 'At least one line item is required',
          warning: null,
        };
      }

      // Only validate budgets for line items marked for delivery
      // In advanced mode: only count items explicitly marked for delivery (markedForDelivery === true)
      // In normal mode: count all items except those explicitly excluded (markedForDelivery !== false)
      const deliveryLineItems = isAdvancedMode
        ? lineItems.filter((item) => item.markedForDelivery === true)
        : lineItems.filter((item) => item.markedForDelivery !== false);

      // If no line items marked for delivery, budgets are valid (0 allocation is okay)
      if (deliveryLineItems.length === 0) {
        return {
          valid: true,
          error: null,
          warning: 'No line items marked for delivery',
          totalAllocated: 0,
          utilization: 0,
          unallocated: campaignBudget,
        };
      }

      return validateLineItemBudgets(deliveryLineItems, campaignBudget);
    }, [lineItems, campaignBudget, isAdvancedMode]);

    // Calculate budget utilization
    const budgetUtilization = useMemo(() => {
      if (!campaignBudget || campaignBudget === 0) return 0;

      // Only include line items marked for delivery
      // In advanced mode: only count items explicitly marked for delivery (markedForDelivery === true)
      // In normal mode: count all items except those explicitly excluded (markedForDelivery !== false)
      const deliveryLineItems = isAdvancedMode
        ? lineItems.filter((item) => item.markedForDelivery === true)
        : lineItems.filter((item) => item.markedForDelivery !== false);
      const totalLineItemBudget = deliveryLineItems.reduce(
        (sum, item) => sum + (parseFloat(item.budgetTotal) || 0),
        0
      );

      return Math.round((totalLineItemBudget / campaignBudget) * 100);
    }, [lineItems, campaignBudget, isAdvancedMode]);

    // Detect flight schedule overlaps
    const flightOverlaps = useMemo(() => {
      return detectFlightOverlaps(flightSchedule);
    }, [flightSchedule]);

    // Calculate remaining budget across all campaigns
    const remainingOrderBudget = useMemo(() => {
      const totalCampaignBudgets = campaigns.reduce(
        (sum, campaign) => sum + (parseFloat(campaign.budget) || 0),
        0
      );
      return orderBudget - totalCampaignBudgets;
    }, [campaigns, orderBudget]);

    // Live impression preview. See `computeCampaignImpressions` for the AV
    // rules (base = campaignBudget/cpm; AV LI contributions added on top
    // except when the campaign itself is fully Value Add, where the base
    // already represents the AV total).
    const campaignImpressions = useMemo(
      () =>
        computeCampaignImpressions({
          campaignBudget,
          campaignCpm,
          lineItems,
          campaignValueAdd,
        }),
      [campaignBudget, campaignCpm, lineItems, campaignValueAdd]
    );

    // Calculate invalid deal IDs (entered but not found in Nexxen publisher deals or malformed)
    const { invalidDealIds, malformedDealIds } = useMemo(() => {
      if (!campaignDealIds?.trim() || publisherDeals.length === 0) {
        return { invalidDealIds: [], malformedDealIds: [] };
      }

      // Regex pattern for valid deal ID format (alphanumeric, dashes, underscores, max 100 chars)
      const dealIdPattern = /^[A-Za-z0-9_-]{1,100}$/;

      // Parse entered deal IDs (accepts commas or newlines)
      const enteredDealIds = campaignDealIds
        .split(/[,\n]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      // Separate malformed and well-formed deal IDs
      const malformed = [];
      const wellFormed = [];
      enteredDealIds.forEach((id) => {
        if (dealIdPattern.test(id)) {
          wellFormed.push(id);
        } else {
          malformed.push(id);
        }
      });

      // Create a set of valid deal IDs from fetched publisher deals
      const validDealIdSet = new Set(publisherDeals.map((deal) => deal.dealId));

      // Find deal IDs that don't exist in the valid set
      const invalid = wellFormed.filter((dealId) => !validDealIdSet.has(dealId));

      return { invalidDealIds: invalid, malformedDealIds: malformed };
    }, [campaignDealIds, publisherDeals]);

    const campaignTypeLabels = {
      1: 'Display',
      7: 'External',
      8: 'Video',
    };

    // Only allow deletion if:
    // 1. There are multiple campaigns (must have at least 1 campaign)
    // 2. AND it's not the first campaign (Campaign 1 at index 0 cannot be deleted)
    const canDelete = campaignCount > 1 && campaignIndex > 0;

    // Handle clone
    const handleCloneClick = () => {
      if (onClone) {
        onClone(campaignIndex);
      }
    };

    // Handle campaign type change with confirmation
    const handleCampaignTypeChange = (newType) => {
      // CRITICAL: Don't clear ad groups during cloning initialization
      // When cloning, form.setFieldsValue triggers this onChange handler
      // but we want to preserve the cloned ad groups
      if (helpers?.isCloning && helpers.isCloning()) {
        // During clone, just update the previousType tracker
        // The form values are already being set by handleInitialValues
        setPreviousType(newType);
        return;
      }

      // Helper function to update type and CPM together
      const updateTypeAndCpm = () => {
        // Get new CPM for display (used for line items which don't store contractCPM)
        const newCampaignCpm = selectedAdvertiser
          ? getCpmForCampaignType(selectedAdvertiser, newType)
          : 7;

        // Get pricing fields (both cpm AND contractCPM) for the campaign
        const pricingFields = selectedAdvertiser
          ? getPricingFieldsForSave(newCampaignCpm, selectedAdvertiser, newType)
          : { cpm: 7, contractCPM: 7, advertiserPercentage: null, contractPercentage: null };

        // Get all campaigns from form
        const allCampaigns = form.getFieldValue('campaigns') || [];

        // Update the campaign and its line items
        const updatedCampaigns = allCampaigns.map((campaign, idx) => {
          if (idx === campaignIndex) {
            return {
              ...campaign,
              type: newType,
              // Campaign level: set both cpm and contractCPM
              cpm: pricingFields.cpm,
              contractCPM: pricingFields.contractCPM,
              advertiserPercentage: pricingFields.advertiserPercentage,
              contractPercentage: pricingFields.contractPercentage,
              lineItems: (campaign.lineItems || []).map((lineItem) => ({
                ...lineItem,
                cpm: newCampaignCpm, // Line items only have cpm field (not contractCPM)
                adGroup: null, // Clear entire ad group (ID, clickthrough, and all creatives)
              })),
            };
          }
          return campaign;
        });

        // Update form with new values
        form.setFieldsValue({ campaigns: updatedCampaigns });
        setPreviousType(newType);

        // Trigger validation to show errors immediately
        setTimeout(() => {
          form.validateFields([['campaigns', campaignIndex, 'cpm']]).catch(() => {
            // Ignore validation errors - just showing them to user
          });
        }, 0);
      };

      // Check if there are any creatives in line items
      const hasCreatives = lineItems.some(
        (lineItem) =>
          lineItem.adGroup?.adGroupCreatives && lineItem.adGroup.adGroupCreatives.length > 0
      );

      // If no creatives exist, just update type and CPM
      if (!hasCreatives) {
        updateTypeAndCpm();
        return;
      }

      // Show confirmation if creatives exist
      confirm({
        title: 'Change Campaign Type?',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>
              Changing the campaign type will remove all uploaded creatives from line items in this
              campaign.
            </p>
            <p>
              <strong>This action cannot be undone.</strong>
            </p>
            <p>Do you want to continue?</p>
          </div>
        ),
        okText: 'Yes, Change Type',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          updateTypeAndCpm();
        },
        onCancel() {
          // Revert to previous type if cancelled
          form.setFieldValue(['campaigns', campaignIndex, 'type'], previousType);
        },
      });
    };

    // Handle delete with confirmation
    const handleDeleteClick = () => {
      confirm({
        title: 'Delete Campaign?',
        icon: <ExclamationCircleOutlined />,
        content: `Are you sure you want to delete Campaign ${campaignIndex + 1}?`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          onDelete(campaignIndex);
        },
      });
    };

    // Handle "Same as campaign order name" checkbox change
    const handleCampaignOrderNameCheckbox = (e) => {
      const checked = e.target.checked;
      setUseCampaignOrderName(checked);

      if (checked && campaignOrderName) {
        // Copy campaign order name to campaign name, adding $ suffix if campaign is value add
        const currentValueAdd = form.getFieldValue(['campaigns', campaignIndex, 'valueAdd']);
        const nameWithSuffix =
          currentValueAdd && !campaignOrderName.endsWith('-$')
            ? `${campaignOrderName}-$`
            : campaignOrderName;

        form.setFieldValue(['campaigns', campaignIndex, 'name'], nameWithSuffix);
        // Clear validation error by validating the field after setting the value
        setTimeout(() => {
          form.validateFields([['campaigns', campaignIndex, 'name']]).catch(() => {
            // Ignore validation errors
          });
        }, 0);
      } else if (!checked) {
        // Keep the current name when unchecked so the user can edit from it —
        // clearing forced everyone to retype the whole name from scratch
      }
    };

    // Handle campaign value add toggle with cascading to all line items
    const handleCampaignValueAdd = () => {
      const currentValueAdd = form.getFieldValue(['campaigns', campaignIndex, 'valueAdd']);
      const newValueAdd = !currentValueAdd;

      // Get current campaign and all its line items
      const allCampaigns = form.getFieldValue('campaigns') || [];
      const updatedCampaigns = allCampaigns.map((campaign, idx) => {
        if (idx === campaignIndex) {
          // Update campaign name with $ suffix
          let updatedCampaignName = campaign.name ? campaign.name.trim() : '';

          if (newValueAdd) {
            // Toggle ON: Add -$ to campaign name
            if (!updatedCampaignName.endsWith('-$')) {
              updatedCampaignName = updatedCampaignName + '-$';
            }
          } else {
            // Toggle OFF: Remove -$ from campaign name
            if (updatedCampaignName.endsWith('-$')) {
              updatedCampaignName = updatedCampaignName.slice(0, -2).trim();
            }
          }

          // Cascade to all line items (except origin line items)
          const updatedLineItems = (campaign.lineItems || []).map((lineItem) => {
            // Skip origin line items - they cannot be marked as value add
            if (lineItem.isOrigin) {
              return lineItem;
            }

            let updatedLineItemName = lineItem.name ? lineItem.name.trim() : '';

            if (newValueAdd) {
              // Toggle ON: Add -$ to line item name
              if (!updatedLineItemName.endsWith('-$')) {
                updatedLineItemName = updatedLineItemName + '-$';
              }
            } else {
              // Toggle OFF: Remove -$ from line item name
              if (updatedLineItemName.endsWith('-$')) {
                updatedLineItemName = updatedLineItemName.slice(0, -2).trim();
              }
            }

            return {
              ...lineItem,
              name: updatedLineItemName,
              valueAdd: newValueAdd,
            };
          });

          return {
            ...campaign,
            name: updatedCampaignName,
            valueAdd: newValueAdd,
            lineItems: updatedLineItems,
          };
        }
        return campaign;
      });

      form.setFieldsValue({ campaigns: updatedCampaigns });

      // Trigger validation to update form state
      setTimeout(() => {
        form.validateFields().catch(() => {
          // Ignore validation errors
        });
      }, 0);
    };

    // Handle applying campaign deal IDs to all line items
    const handleApplyDealIdsToLineItems = () => {
      if (!campaignDealIds?.trim()) return;

      // Regex pattern for valid deal IDs (alphanumeric, dashes, underscores, max 100 chars)
      const dealIdPattern = /^[A-Za-z0-9_-]{1,100}$/;

      // Parse and sanitize deal IDs (commas or newlines)
      const dealIdList = campaignDealIds
        .split(/[,\n]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0 && dealIdPattern.test(id));

      if (dealIdList.length === 0) {
        message.warning(
          'No valid deal IDs to apply. Deal IDs must be alphanumeric (dashes and underscores allowed).'
        );
        return;
      }

      // Filter to only valid deal IDs (exist in publisherDeals)
      const validDealIdSet = new Set(publisherDeals.map((deal) => deal.dealId));
      const validDealIdList = dealIdList.filter((dealId) => validDealIdSet.has(dealId));

      if (validDealIdList.length === 0) {
        message.warning(
          'No valid deal IDs to apply. All entered deal IDs are not found in Nexxen.'
        );
        return;
      }

      // Get all campaigns from form
      const allCampaigns = form.getFieldValue('campaigns') || [];

      // Track statistics
      let totalApplied = 0;
      let totalSkipped = 0;
      let totalFixed = 0;

      // Get current line items for this campaign
      const currentLineItems = allCampaigns[campaignIndex]?.lineItems || [];

      // Process each line item and update only dspDealIds using targeted form.setFieldValue
      currentLineItems.forEach((lineItem, lineItemIndex) => {
        const existingDealIds = lineItem.dspDealIds || [];

        // Create a map of existing deals by dealId for quick lookup
        const existingDealMap = new Map(existingDealIds.map((d) => [d.dealId, d]));

        // Process each valid deal ID
        const updatedDealIds = [];
        const processedDealIds = new Set();

        validDealIdList.forEach((dealId) => {
          processedDealIds.add(dealId);
          const existingDeal = existingDealMap.get(dealId);
          const dealInfo = publisherDeals.find((d) => d.dealId === dealId);

          if (existingDeal) {
            // Deal exists - check if it needs dspInternalId fix
            if (!existingDeal.dspInternalId && dealInfo?.id) {
              // Fix: add missing dspInternalId
              totalFixed++;
              updatedDealIds.push({
                ...existingDeal,
                dspInternalId: dealInfo.id,
                name: dealInfo.name || existingDeal.name || dealId,
              });
            } else {
              // Already complete, keep as-is
              totalSkipped++;
              updatedDealIds.push(existingDeal);
            }
          } else {
            // New deal - add it
            totalApplied++;
            updatedDealIds.push({
              dspInternalId: dealInfo?.id || null,
              dealId: dealId,
              name: dealInfo?.name || dealId,
              cpmBid: dealInfo?.price || null,
            });
          }
        });

        // Keep existing deals that weren't in the input list
        existingDealIds.forEach((deal) => {
          if (!processedDealIds.has(deal.dealId)) {
            updatedDealIds.push(deal);
          }
        });

        // Use targeted setFieldValue to only update dspDealIds, preserving other fields
        form.setFieldValue(
          ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'dspDealIds'],
          updatedDealIds
        );
      });

      const lineItemCount = allCampaigns[campaignIndex]?.lineItems?.length || 0;
      const skippedInvalid = dealIdList.length - validDealIdList.length;

      // Build descriptive message
      if (totalApplied === 0 && totalFixed === 0 && totalSkipped > 0) {
        message.info(
          `All ${validDealIdList.length} deal ID(s) already exist in ${lineItemCount} line item(s). Nothing to apply.`
        );
      } else if (totalFixed > 0) {
        const parts = [];
        if (totalApplied > 0) parts.push(`Added ${totalApplied} new`);
        parts.push(`Fixed ${totalFixed} existing deal(s) with missing Nexxen ID`);
        if (totalSkipped > 0) parts.push(`${totalSkipped} unchanged`);
        message.success(parts.join(', '));
      } else if (totalSkipped > 0 || skippedInvalid > 0) {
        const parts = [`Applied ${totalApplied} deal ID(s) to ${lineItemCount} line item(s)`];
        if (totalSkipped > 0) parts.push(`${totalSkipped} already existed`);
        if (skippedInvalid > 0) parts.push(`${skippedInvalid} invalid`);
        message.success(parts.join(', '));
      } else {
        message.success(`Applied ${totalApplied} deal ID(s) to ${lineItemCount} line item(s)`);
      }
    };

    // Handle clearing all deal IDs from line items
    const handleClearAllDealIds = () => {
      const allCampaigns = form.getFieldValue('campaigns') || [];
      const currentLineItems = allCampaigns[campaignIndex]?.lineItems || [];

      // Count total deals to be cleared
      let totalDeals = 0;
      currentLineItems.forEach((lineItem) => {
        totalDeals += (lineItem.dspDealIds || []).length;
      });

      // Clear dspDealIds from each line item
      currentLineItems.forEach((lineItem, lineItemIndex) => {
        if ((lineItem.dspDealIds || []).length > 0) {
          form.setFieldValue(
            ['campaigns', campaignIndex, 'lineItems', lineItemIndex, 'dspDealIds'],
            []
          );
        }
      });

      // Always clear the campaign deal IDs input
      setCampaignDealIds('');

      if (totalDeals === 0) {
        message.info('Deal ID input cleared');
      } else {
        message.success(
          `Cleared ${totalDeals} deal ID(s) from ${currentLineItems.length} line item(s)`
        );
      }
    };

    // Handle ad groups selected from the dialog - store for preview
    const handleAdGroupsSelected = (adGroups) => {
      if (!adGroups || adGroups.length === 0) {
        return;
      }
      setSelectedAdGroups(adGroups);
      // Initialize click-through URLs as empty for each ad group
      const clickthroughs = {};
      adGroups.forEach((ag) => {
        clickthroughs[ag.name] = '';
      });
      setAdGroupClickthroughs(clickthroughs);
      // Select all line items by default
      setSelectedLineItemIndices(lineItems.map((_, idx) => idx));
    };

    // Handle removing an ad group from selection
    const handleRemoveSelectedAdGroup = (adGroupName) => {
      setSelectedAdGroups((prev) => prev.filter((ag) => ag.name !== adGroupName));
      setAdGroupClickthroughs((prev) => {
        const updated = { ...prev };
        delete updated[adGroupName];
        return updated;
      });
    };

    // Handle clearing all selected ad groups
    const handleClearSelectedAdGroups = () => {
      setSelectedAdGroups([]);
      setAdGroupClickthroughs({});
      setSelectedLineItemIndices([]);
    };

    // Handle applying selected ad groups to selected line items
    const handleApplyAdGroupsToLineItems = () => {
      if (selectedAdGroups.length === 0) {
        message.warning('Please select ad groups first');
        return;
      }

      if (selectedLineItemIndices.length === 0) {
        message.warning('Please select at least one line item to apply to');
        return;
      }

      const currentCampaigns = form.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
        if (idx === campaignIndex) {
          return {
            ...campaign,
            lineItems: campaign.lineItems.map((li, liIdx) => {
              // Only apply to selected line items
              if (!selectedLineItemIndices.includes(liIdx)) {
                return li;
              }

              // Create ad group creatives from selected ad groups with click-through URLs
              const adGroupCreatives = selectedAdGroups.map((adGroup) => {
                // Check if creative already exists in the line item
                const existingCreative = li.adGroup?.adGroupCreatives?.find(
                  (a) => a.name === adGroup.name
                );

                if (existingCreative) {
                  // Update click-through if provided
                  const clickthrough = adGroupClickthroughs[adGroup.name];
                  if (clickthrough) {
                    return { ...existingCreative, clickthrough };
                  }
                  return existingCreative;
                }

                // Clone and prepare the ad group without IDs
                const clonedAdGroup = JSON.parse(JSON.stringify(adGroup));
                delete clonedAdGroup.id;
                if (clonedAdGroup.medias) {
                  clonedAdGroup.medias = clonedAdGroup.medias.map((media) => {
                    delete media.id;
                    delete media.dspCreativeId;
                    return media;
                  });
                }

                return {
                  ...clonedAdGroup,
                  clickthrough: adGroupClickthroughs[adGroup.name] || '',
                  startDate: li.startDate ? moment(li.startDate) : moment(orderDates.startDate),
                  endDate: li.endDate ? moment(li.endDate) : moment(orderDates.endDate),
                };
              });

              return {
                ...li,
                adGroup: {
                  ...(li.adGroup || {}),
                  adGroupCreatives,
                },
              };
            }),
          };
        }
        return campaign;
      });

      form.setFieldsValue({ campaigns: updatedCampaigns });

      const lineItemCount = selectedLineItemIndices.length;
      message.success(
        `Applied ${selectedAdGroups.length} ad group(s) to ${lineItemCount} line item${lineItemCount > 1 ? 's' : ''}`
      );

      // Clear the selection after applying
      handleClearSelectedAdGroups();
    };

    // Handle clearing creatives from all line items
    const handleClearAllCreatives = () => {
      confirm({
        title: 'Clear All Creatives',
        icon: <ExclamationCircleOutlined />,
        content: 'This will remove all creatives from all line items in this campaign. Continue?',
        onOk() {
          const currentCampaigns = form.getFieldValue('campaigns') || [];
          const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
            if (idx === campaignIndex) {
              return {
                ...campaign,
                lineItems: campaign.lineItems.map((li) => ({ ...li, adGroup: null })),
              };
            }
            return campaign;
          });
          form.setFieldsValue({ campaigns: updatedCampaigns });
          message.success('All creatives cleared');
        },
      });
    };

    // Handle applying bulk targeting type to selected line items
    const handleApplyTargetingType = () => {
      if (!bulkTargetingType) {
        message.warning('Please select a targeting type first');
        return;
      }

      if (targetingLineItemIndices.length === 0) {
        message.warning('Please select at least one line item to apply to');
        return;
      }

      // Validate that required targeting data is provided based on type
      if (bulkTargetingType === 'COUNTRY' && !bulkTargetingCountry) {
        message.warning('Please select a country');
        return;
      }
      if (bulkTargetingType === 'REGION' && selectedBulkRegionGroups.length === 0) {
        message.warning('Please select region groups');
        return;
      }
      if (bulkTargetingType === 'LOCAL' && selectedBulkTargetingGroups.length === 0) {
        message.warning('Please select targeting groups');
        return;
      }

      const currentCampaigns = form.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
        if (idx === campaignIndex) {
          return {
            ...campaign,
            lineItems: campaign.lineItems.map((li, liIdx) => {
              if (!targetingLineItemIndices.includes(liIdx)) {
                return li;
              }

              // Build the targeting update based on type
              const targetingUpdate = { targetingType: bulkTargetingType };

              if (bulkTargetingType === 'COUNTRY') {
                targetingUpdate.targetingCountries = bulkTargetingCountry;
                // Clear other targeting data
                targetingUpdate.targetingRegions = null;
                targetingUpdate.targetingLocations = null;
              } else if (bulkTargetingType === 'REGION') {
                targetingUpdate.targetingRegions = selectedBulkRegionGroups;
                // Clear other targeting data
                targetingUpdate.targetingCountries = null;
                targetingUpdate.targetingLocations = null;
              } else if (bulkTargetingType === 'LOCAL') {
                targetingUpdate.targetingLocations = selectedBulkTargetingGroups;
                // Clear other targeting data
                targetingUpdate.targetingCountries = null;
                targetingUpdate.targetingRegions = null;
              }

              return { ...li, ...targetingUpdate };
            }),
          };
        }
        return campaign;
      });

      form.setFieldsValue({ campaigns: updatedCampaigns });

      const count = targetingLineItemIndices.length;
      const typeLabel =
        bulkTargetingType === 'COUNTRY'
          ? 'country targeting'
          : bulkTargetingType === 'REGION'
            ? 'region targeting'
            : 'local targeting';
      message.success(`Applied ${typeLabel} to ${count} line item${count > 1 ? 's' : ''}`);

      // Reset all targeting state
      setBulkTargetingType(null);
      setTargetingLineItemIndices([]);
      setBulkTargetingCountry(null);
      setSelectedBulkRegionGroups([]);
      setSelectedBulkTargetingGroups([]);
    };

    // Handle applying bulk audience type to selected line items
    const handleApplyAudienceType = () => {
      if (!bulkAudienceType) {
        message.warning('Please select an audience type first');
        return;
      }

      if (audienceLineItemIndices.length === 0) {
        message.warning('Please select at least one line item to apply to');
        return;
      }

      const currentCampaigns = form.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
        if (idx === campaignIndex) {
          return {
            ...campaign,
            lineItems: campaign.lineItems.map((li, liIdx) => {
              if (!audienceLineItemIndices.includes(liIdx)) {
                return li;
              }
              return { ...li, audienceType: bulkAudienceType };
            }),
          };
        }
        return campaign;
      });

      form.setFieldsValue({ campaigns: updatedCampaigns });

      const count = audienceLineItemIndices.length;
      message.success(`Applied audience type to ${count} line item${count > 1 ? 's' : ''}`);
      setBulkAudienceType(null);
      setAudienceLineItemIndices([]);
    };

    return (
      <>
        <Card
          style={{
            marginBottom: 16,
            border: '2px solid #e8e8e8',
            borderRadius: '8px',
          }}
          bodyStyle={{ padding: '16px' }}
        >
          <Row gutter={[16, 16]}>
            {/* Campaign Header Row */}
            <Col span={24}>
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <Space style={{ whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 16, whiteSpace: 'nowrap' }}>
                    Campaign {campaignIndex + 1}
                  </Text>
                  {campaignType && (
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      ({campaignTypeLabels[campaignType]})
                    </Text>
                  )}
                  {/* Value Add Tag */}
                  {campaignValueAdd && (
                    <Tag icon={<GiftOutlined />} color="purple">
                      Value Add
                    </Tag>
                  )}
                  {/* Pricing Model Indicator - Advanced Mode Only */}
                  {isAdvancedMode && pricingModel && (
                    <Tooltip title={pricingModel.tooltip}>
                      <Tag color={pricingModel.color} style={{ marginLeft: 8 }}>
                        {pricingModel.name}: {pricingModel.description}
                      </Tag>
                    </Tooltip>
                  )}
                  {/* Launch Type Indicator - Advanced Mode Only, shown for specific statuses */}
                  {isAdvancedMode &&
                    isLaunchProgrammatically !== undefined &&
                    campaignStatus &&
                    ['LIVE', 'LIVE_PENDING', 'LIVE_APPROVED', 'PAUSED'].includes(
                      campaignStatus
                    ) && (
                      <Tooltip
                        title={
                          isLaunchProgrammatically
                            ? 'Campaign is launched programmatically via DSP integration'
                            : 'Campaign is launched manually by operations team'
                        }
                      >
                        <Tag
                          color={isLaunchProgrammatically ? 'purple' : 'default'}
                          style={{ marginLeft: 8 }}
                        >
                          {isLaunchProgrammatically ? 'Programmatic Launch' : 'Manual Launch'}
                        </Tag>
                      </Tooltip>
                    )}
                </Space>
                <Space style={{ flexShrink: 0 }}>
                  {onClone && (
                    <Tooltip title="Clone Campaign">
                      <Button
                        shape="circle"
                        type="default"
                        icon={<CopyOutlined />}
                        onClick={handleCloneClick}
                      />
                    </Tooltip>
                  )}
                  {/* Value Add button - visible when user has permission and status is not DRAFT */}
                  {hasTraffickerOrProcessorPermission && isNotDraftStatus && (
                    <Tooltip
                      title={
                        campaignValueAdd
                          ? 'Marked as Value Add Campaign'
                          : 'Mark as Value Add Campaign'
                      }
                    >
                      <Button
                        shape="circle"
                        icon={<GiftOutlined />}
                        style={{
                          backgroundColor: campaignValueAdd ? '#722ed1' : 'transparent',
                          borderColor: campaignValueAdd ? '#722ed1' : '#d9d9d9',
                          color: campaignValueAdd ? '#fff' : '#722ed1',
                        }}
                        onClick={handleCampaignValueAdd}
                      />
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete Campaign">
                      <Button
                        shape="circle"
                        type="default"
                        icon={<DeleteOutlined />}
                        onClick={handleDeleteClick}
                      />
                    </Tooltip>
                  )}
                </Space>
              </div>
            </Col>

            {/* Campaign Configuration Row 1: Type and Name */}
            <Col span={24}>
              <Row gutter={16}>
                {/* Campaign Type */}
                <Col xs={24} sm={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>Campaign Type *</Text>
                    <Form.Item
                      name={[campaignIndex, 'type']}
                      fieldKey={[campaignIndex, 'type']}
                      rules={[{ required: true, message: 'Campaign type is required' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        style={{ width: '100%' }}
                        showSearch
                        placeholder="Select type"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={handleCampaignTypeChange}
                        options={[
                          { label: 'Display', value: 1, key: 1 },
                          { label: 'External', value: 7, key: 7 },
                          { label: 'Video', value: 8, key: 8 },
                        ]}
                      />
                    </Form.Item>
                  </Space>
                </Col>

                {/* Campaign Name */}
                <Col xs={24} sm={18}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>Campaign Name *</Text>
                    <Form.Item
                      name={[campaignIndex, 'name']}
                      fieldKey={[campaignIndex, 'name']}
                      rules={[
                        { required: true, message: 'Campaign name is required' },
                        { max: 100, message: 'Campaign name must be 100 characters or less' },
                      ]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input
                        placeholder={`Campaign ${campaignIndex + 1}`}
                        maxLength={100}
                        onChange={(e) => {
                          // Typing a custom name breaks the "same as campaign order
                          // name" link — no need to hunt for the checkbox first
                          if (useCampaignOrderName) {
                            setUseCampaignOrderName(false);
                          }
                          // Strip $ symbols - they should only be added programmatically
                          const cleanValue = e.target.value.replace(/\$/g, '');
                          if (cleanValue !== e.target.value) {
                            form.setFieldValue(['campaigns', campaignIndex, 'name'], cleanValue);
                          }
                        }}
                        onBlur={() => {
                          // Re-add $ to Value Add campaigns if it was removed during editing
                          const currentValueAdd = form.getFieldValue([
                            'campaigns',
                            campaignIndex,
                            'valueAdd',
                          ]);
                          if (currentValueAdd) {
                            const currentName =
                              form.getFieldValue(['campaigns', campaignIndex, 'name'])?.trim() ||
                              '';
                            if (!currentName.endsWith('-$')) {
                              form.setFieldValue(
                                ['campaigns', campaignIndex, 'name'],
                                currentName + '-$'
                              );
                            }
                          }
                        }}
                      />
                    </Form.Item>
                    <Checkbox
                      checked={useCampaignOrderName}
                      onChange={handleCampaignOrderNameCheckbox}
                      disabled={!campaignOrderName}
                    >
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Same as campaign order name
                      </Text>
                    </Checkbox>
                  </Space>
                </Col>
              </Row>
            </Col>

            {/* Campaign Configuration Row 2: Budget and Dates */}
            <Col span={24}>
              <Row gutter={16} align="stretch">
                {/* Campaign Budget */}
                <Col xs={24} sm={8}>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Space wrap>
                      <Text strong>Budget *</Text>
                      <Tooltip title="Budget allocated to this campaign. Line items within this campaign must not exceed this amount.">
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </Space>
                    <div style={{ marginTop: 'auto' }}>
                      <Form.Item
                        name={[campaignIndex, 'budget']}
                        fieldKey={[campaignIndex, 'budget']}
                        rules={[
                          { required: true, message: 'Budget is required' },
                          {
                            validator: async (_, value) => {
                              if (value === 0) {
                                throw new Error('Budget must be greater than 0');
                              }
                              if (value && value < 0) {
                                throw new Error('Budget must be positive');
                              }
                              if (
                                value &&
                                remainingOrderBudget < 0 &&
                                campaigns[campaignIndex]?.budget !== value
                              ) {
                                throw new Error('Total campaign budgets exceed order budget');
                              }
                            },
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="0.00"
                          prefix="$"
                          min={0}
                          precision={2}
                          status={campaignBudget === 0 ? 'error' : undefined}
                          formatter={(value) =>
                            value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
                          }
                          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                      {campaignBudget > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {budgetUtilization}% allocated to line items
                        </Text>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Campaign Start Date (Optional) */}
                <Col xs={24} sm={8}>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Space wrap>
                      <Text strong>Start Date</Text>
                      <Tooltip title="Optional. Leave empty to inherit from order start date.">
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </Space>
                    <div style={{ marginTop: 'auto' }}>
                      <Form.Item
                        name={[campaignIndex, 'startDate']}
                        fieldKey={[campaignIndex, 'startDate']}
                        style={{ marginBottom: 0 }}
                      >
                        <TimezoneDatePicker
                          timezone={timezone}
                          style={{ width: '100%' }}
                          placeholder="Order start"
                          format="MM/DD/YYYY"
                          onChange={(date) => {
                            // Normalize to start of day in user timezone, then convert to UTC
                            const normalizedDate = date
                              ? moment.tz(date.format('YYYY-MM-DD'), timezone).startOf('day').utc()
                              : null;
                            form.setFieldValue(
                              ['campaigns', campaignIndex, 'startDate'],
                              normalizedDate
                            );
                          }}
                          disabledDate={(current) => {
                            if (!orderDates.startDate || !orderDates.endDate) return false;
                            return (
                              current < moment(orderDates.startDate).tz(timezone).startOf('day') ||
                              current > moment(orderDates.endDate).tz(timezone).endOf('day')
                            );
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </Col>

                {/* Campaign End Date (Optional) */}
                <Col xs={24} sm={8}>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Space wrap>
                      <Text strong>End Date</Text>
                      <Tooltip title="Optional. Leave empty to inherit from order end date.">
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </Space>
                    <div style={{ marginTop: 'auto' }}>
                      <Form.Item
                        name={[campaignIndex, 'endDate']}
                        fieldKey={[campaignIndex, 'endDate']}
                        style={{ marginBottom: 0 }}
                      >
                        <TimezoneDatePicker
                          timezone={timezone}
                          style={{ width: '100%' }}
                          placeholder="Order end"
                          format="MM/DD/YYYY"
                          onChange={(date) => {
                            // Normalize to end of day in user timezone, then convert to UTC
                            const normalizedDate = date
                              ? moment.tz(date.format('YYYY-MM-DD'), timezone).endOf('day').utc()
                              : null;
                            form.setFieldValue(
                              ['campaigns', campaignIndex, 'endDate'],
                              normalizedDate
                            );

                            // Cascade: clamp this campaign's line items and flight schedules
                            // that exceed the new campaign end date (no-op on extension)
                            if (normalizedDate) {
                              const allCampaigns = form.getFieldValue('campaigns') || [];
                              form.setFieldsValue({
                                campaigns: allCampaigns.map((c, cIdx) =>
                                  cIdx === campaignIndex
                                    ? clampCampaignToEndDate(c, normalizedDate)
                                    : c
                                ),
                              });
                            }
                          }}
                          disabledDate={(current) => {
                            if (!orderDates.startDate || !orderDates.endDate) return false;
                            const startDate = campaignStartDate || orderDates.startDate;
                            return (
                              current < moment(startDate).tz(timezone).startOf('day') ||
                              current > moment(orderDates.endDate).tz(timezone).endOf('day')
                            );
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>

            {/* CPM and Impressions Row - shown for CPM pricing model */}
            {!hideCpmFields && (
              <Col span={24}>
                <Row gutter={16} align="stretch">
                  {/* Campaign CPM */}
                  <Col xs={24} sm={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <div>
                        <Text strong>CPM</Text>
                        <div>
                          <Text type="secondary">Rate per 1,000 impressions</Text>
                        </div>
                      </div>
                      <div style={{ marginTop: 'auto' }}>
                        <Tooltip title={!canEditCpm ? 'Only Ad Ops users can edit CPM' : ''}>
                          <Form.Item
                            name={[campaignIndex, 'cpm']}
                            fieldKey={[campaignIndex, 'cpm']}
                            rules={[
                              {
                                required: !hideCpmFields,
                                message: 'CPM is required',
                              },
                              {
                                validator: async (_, value) => {
                                  // Skip validation for Percentage pricing model
                                  if (hideCpmFields) {
                                    return Promise.resolve();
                                  }

                                  // Allow empty values (required rule handles this)
                                  if (value === '' || value === null || value === undefined) {
                                    return Promise.resolve();
                                  }

                                  // Check if advertiser is selected
                                  if (!selectedAdvertiser) {
                                    throw new Error('Please select an advertiser first');
                                  }

                                  // Get minimum CPM from advertiser for this campaign type
                                  const minimumCpm = getMinimumCpmForCampaignType(
                                    selectedAdvertiser,
                                    campaignType
                                  );

                                  // Validate against minimum
                                  if (value < minimumCpm) {
                                    const campaignTypeLabel =
                                      campaignType === 1
                                        ? 'Display'
                                        : campaignType === 7
                                          ? 'External'
                                          : campaignType === 8
                                            ? 'Video'
                                            : 'this campaign type';
                                    throw new Error(
                                      `CPM must be at least $${minimumCpm.toFixed(2)} (advertiser minimum for ${campaignTypeLabel})`
                                    );
                                  }

                                  return Promise.resolve();
                                },
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="7.00"
                              min={0}
                              max={Math.pow(10, 12) - 1}
                              precision={2}
                              disabled={!canEditCpm}
                              formatter={(value) => {
                                if (!value && value !== 0) return '';
                                return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                              }}
                              parser={(value) =>
                                value ? value.replace(/\$\s?|(,*)/g, '') : undefined
                              }
                            />
                          </Form.Item>
                        </Tooltip>
                      </div>
                    </div>
                  </Col>

                  {/* Campaign Impressions (calculated) */}
                  <Col xs={24} sm={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <div>
                        <Text strong>Impressions</Text>
                        <div>
                          <Text type="secondary">Campaign total</Text>
                        </div>
                      </div>
                      <div style={{ marginTop: 'auto' }}>
                        <Input
                          style={{ width: '100%' }}
                          disabled
                          value={(campaignImpressions || 0).toLocaleString()}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            )}

            {/* Pricing Row - shown for Percentage pricing model */}
            {hideCpmFields && (
              <Col span={24}>
                <Row gutter={16} align="stretch">
                  {/* Auction Plus Percentage Field */}
                  <Col xs={24} sm={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <div>
                        <Text strong>Auction Plus Percentage</Text>
                        <div>
                          <Text type="secondary">Override auction plus %</Text>
                        </div>
                      </div>
                      <div style={{ marginTop: 'auto' }}>
                        <Tooltip title={!canEditCpm ? 'Only Ad Ops users can edit percentage' : ''}>
                          <Form.Item
                            name={[campaignIndex, 'advertiserPercentage']}
                            fieldKey={[campaignIndex, 'advertiserPercentage']}
                            rules={[
                              { required: true, message: 'Auction Plus Percentage is required' },
                              {
                                validator: (_, value) =>
                                  validatePercentageOverride(value, selectedAdvertiser),
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder={
                                selectedAdvertiser
                                  ? `${getPercentageForAdvertiser(selectedAdvertiser) || 0}`
                                  : '0'
                              }
                              min={0}
                              max={99.99}
                              precision={2}
                              disabled={!canEditCpm}
                              suffix="%"
                            />
                          </Form.Item>
                        </Tooltip>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            )}

            {/* Budget Warning - appears right after budget row - "No line items marked for delivery" only in advanced mode */}
            {showBudgetWarning &&
              budgetValidation.valid &&
              budgetValidation.warning &&
              (isAdvancedMode ||
                budgetValidation.warning !== 'No line items marked for delivery') && (
                <Col span={24}>
                  <Alert
                    type="warning"
                    message="Budget Warning"
                    description={budgetValidation.warning}
                    showIcon
                    style={{ marginBottom: 0 }}
                  />
                </Col>
              )}

            {/* Media Plan Strategy Toggle */}
            <Col span={24}>
              <Row gutter={16}>
                <Col span={24}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space align="center">
                      <Text strong>Has Media Plan Strategy</Text>
                      <Form.Item
                        name={[campaignIndex, 'hasMediaPlanStrategy']}
                        fieldKey={[campaignIndex, 'hasMediaPlanStrategy']}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Switch />
                      </Form.Item>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Enable this to indicate that Ad Ops will handle the strategic media planning.
                      Line items become optional when enabled.
                    </Text>
                    {hasMediaPlanStrategy && (
                      <>
                        <Alert
                          message="Media Plan Strategy Enabled"
                          description="Line items are now optional. You can add line items as guidance for Ad Ops, or leave them empty for Ad Ops to create."
                          type="info"
                          showIcon
                        />
                        {canSeeMediaPlan && (
                          <MediaPlanWidget
                            objective={planObjective}
                            budget={campaignBudget || orderBudget}
                            startDate={campaignStartDate || orderDates?.startDate}
                            endDate={campaignEndDate || orderDates?.endDate}
                            advertiserId={advertiserOrg?.id}
                            advertiserName={advertiserOrg?.name}
                            campaignName={campaignName}
                            category={researchCategory}
                            advertiserDomain={advertiserOrg?.domain}
                            geo={researchGeo}
                            city={researchCity}
                          />
                        )}
                      </>
                    )}
                  </Space>
                </Col>
              </Row>
            </Col>

            {/* DSP Configuration - Advanced Mode Only */}
            {isAdvancedMode && (
              <Col span={24}>
                <Row gutter={16} align="stretch">
                  <Col xs={24} sm={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Space wrap>
                        <Text strong>Demand-Side Platform</Text>
                        <Tooltip title="Select the DSP for programmatic launching of this campaign">
                          <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                        </Tooltip>
                      </Space>
                      <div style={{ marginTop: 'auto' }}>
                        <Form.Item
                          name={[campaignIndex, 'demandSidePlatform']}
                          fieldKey={[campaignIndex, 'demandSidePlatform']}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            style={{ width: '100%' }}
                            placeholder="Select DSP"
                            allowClear
                            options={[
                              { value: 'NEXXEN', label: 'NEXXEN' },
                              { value: 'LIQUIDM', label: 'LIQUIDM' },
                              { value: 'JAMLOOP', label: 'JAMLOOP' },
                            ]}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Space wrap>
                        <Text strong>Ad Provider ID</Text>
                        <Tooltip title="Unique identifier for the ad provider">
                          <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                        </Tooltip>
                      </Space>
                      <div style={{ marginTop: 'auto' }}>
                        <Form.Item
                          name={[campaignIndex, 'adProviderId']}
                          fieldKey={[campaignIndex, 'adProviderId']}
                          style={{ marginBottom: 0 }}
                        >
                          <Input style={{ width: '100%' }} placeholder="Enter Ad Provider ID" />
                        </Form.Item>
                      </div>
                    </div>
                  </Col>
                  {!hideCpmFields && (
                    <Col xs={24} sm={6}>
                      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Space wrap>
                          <Text strong>Maximum Bid *</Text>
                          <Tooltip title="Maximum bid amount for the campaign (required for CPM pricing)">
                            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                          </Tooltip>
                        </Space>
                        <div style={{ marginTop: 'auto' }}>
                          <Form.Item
                            name={[campaignIndex, 'maxBid']}
                            fieldKey={[campaignIndex, 'maxBid']}
                            rules={[
                              {
                                required: true,
                                message: '',
                              },
                              {
                                type: 'number',
                                min: 0.01,
                                message: '',
                                transform: (value) => {
                                  const num = parseFloat(value);
                                  return isNaN(num) ? undefined : num;
                                },
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="4.00"
                              prefix="$"
                              min={0.01}
                              step={0.01}
                              precision={2}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Col>
            )}

            {/* Campaign-Level Flight Schedule (Advanced Mode + NEXXEN DSP + non-DRAFT + Ad Ops) */}
            {isAdvancedMode &&
              campaignDsp === 'NEXXEN' &&
              isNotDraftStatus &&
              hasTraffickerOrProcessorPermission && (
                <Col span={24}>
                  <Card title="CAMPAIGN FLIGHT SCHEDULE" style={{ marginTop: 8 }}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      {/* Flight Overlap Warning */}
                      {flightOverlaps.length > 0 && (
                        <Alert
                          type="error"
                          message="Overlapping Flight Dates Detected"
                          description={
                            <div>
                              <p>The following flights have overlapping date ranges:</p>
                              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                {flightOverlaps.map((overlap, idx) => (
                                  <li key={idx}>
                                    Flight {overlap.flightIndex1 + 1} and Flight{' '}
                                    {overlap.flightIndex2 + 1}
                                  </li>
                                ))}
                              </ul>
                              <p style={{ marginTop: 8, marginBottom: 0 }}>
                                Please adjust the date ranges to ensure flights do not overlap.
                              </p>
                            </div>
                          }
                          showIcon
                        />
                      )}
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            Configure multiple flight date ranges for phased campaign execution
                            (maps to Nexxen IO schedules)
                          </Text>
                        </div>
                        <Space>
                          <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                              setRenewMonths(1);
                              setRenewMonthlyBudget(null);
                              setRenewEffectiveDate(null);
                              setShowRenewModal(true);
                            }}
                          >
                            Renew
                          </Button>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              let newStartDate, newEndDate;

                              if (flightSchedule.length === 0) {
                                // First flight: use campaign dates (already in UTC from timezone-aware pickers)
                                newStartDate = campaignStartDate
                                  ? moment(campaignStartDate)
                                  : orderDates.startDate
                                    ? moment(orderDates.startDate)
                                    : moment.tz(timezone).startOf('day').utc();
                                newEndDate = campaignEndDate
                                  ? moment(campaignEndDate)
                                  : orderDates.endDate
                                    ? moment(orderDates.endDate)
                                    : moment.tz(timezone).add(2, 'days').endOf('day').utc();
                              } else {
                                // Subsequent flights: start after the furthest end date
                                let furthestEndDate = null;
                                flightSchedule.forEach((flight) => {
                                  if (flight.endDate) {
                                    const flightEnd = moment(flight.endDate);
                                    if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
                                      furthestEndDate = flightEnd;
                                    }
                                  }
                                });

                                if (!furthestEndDate) {
                                  furthestEndDate = campaignEndDate
                                    ? moment(campaignEndDate)
                                    : moment.tz(timezone);
                                }

                                // Convert to user timezone to get the calendar date, then add 1 day, convert back to UTC
                                const nextDay = moment(furthestEndDate)
                                  .tz(timezone)
                                  .add(1, 'day')
                                  .format('YYYY-MM-DD');
                                newStartDate = moment.tz(nextDay, timezone).startOf('day').utc();
                                newEndDate = moment
                                  .tz(nextDay, timezone)
                                  .add(2, 'days')
                                  .endOf('day')
                                  .utc();
                              }

                              const newFlight = {
                                startDate: newStartDate,
                                endDate: newEndDate,
                                budget: null,
                              };

                              // Only add CPM for CPM pricing model
                              if (!hideCpmFields) {
                                newFlight.cpm = campaignCpm || 7;
                              }

                              const updatedFlightSchedule = [...flightSchedule, newFlight];
                              setFlightSchedule(updatedFlightSchedule);

                              // Auto-adjust end dates if the new flight's end date exceeds them
                              autoAdjustEndDatesFromFlightSchedule(updatedFlightSchedule);
                            }}
                          >
                            Add Flight
                          </Button>
                        </Space>
                      </Space>

                      <Modal
                        title="Renew Campaign"
                        visible={showRenewModal}
                        onOk={handleRenewConfirm}
                        onCancel={() => setShowRenewModal(false)}
                        okText="Renew"
                        okButtonProps={{ disabled: !renewalPreview }}
                      >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          <div>
                            <Text>Number of months to renew:</Text>
                            <InputNumber
                              style={{ marginLeft: 8 }}
                              min={1}
                              max={24}
                              value={renewMonths}
                              onChange={(value) => setRenewMonths(value || 1)}
                            />
                          </div>
                          <div>
                            <Text>New monthly budget (optional):</Text>
                            <InputNumber
                              style={{ marginLeft: 8 }}
                              addonBefore="$"
                              min={0.01}
                              precision={2}
                              placeholder="Same as current rate"
                              value={renewMonthlyBudget}
                              onChange={(value) => setRenewMonthlyBudget(value)}
                            />
                          </div>
                          <div>
                            <Text>Rate change effective date (optional):</Text>
                            <DatePicker
                              style={{ marginLeft: 8 }}
                              value={renewEffectiveDate}
                              onChange={(date) => setRenewEffectiveDate(date)}
                              placeholder="First renewal day"
                            />
                          </div>
                          {renewalPreview?.rateChange && (
                            <Alert
                              type="info"
                              showIcon
                              message={`Rate change: $${renewalPreview.rateChange.oldMonthlyBudget.toFixed(2)} → $${renewalPreview.rateChange.newMonthlyBudget.toFixed(2)} per month`}
                              description={
                                renewalPreview.rateChange.split
                                  ? 'The current flight will be split at the effective date with daily proration (old rate before, new rate after).'
                                  : 'The new rate takes effect at the next flight boundary.'
                              }
                            />
                          )}
                          {renewalPreview ? (
                            <Text type="secondary">
                              Adds {renewMonths} monthly flight(s) of the last flight's budget to
                              this campaign. Delivery line items extend at their own monthly rate;
                              reference (customer-facing) line items share the renewal pro-rata.
                              Total added budget: ${renewalPreview.renewalTotal.toFixed(2)} — new
                              end date: {moment(renewalPreview.newEndDate).format('MM/DD/YYYY')}.
                            </Text>
                          ) : (
                            <Text type="warning">No existing flight schedule to renew from.</Text>
                          )}
                        </Space>
                      </Modal>

                      <Table
                        scroll={{ x: 'max-content' }}
                        dataSource={flightSchedule.map((flight, idx) => {
                          const numberOfDays =
                            flight.startDate && flight.endDate
                              ? moment(flight.endDate).diff(moment(flight.startDate), 'days') + 1
                              : null;
                          const impressionGoal =
                            flight.budget && flight.cpm
                              ? Math.round((flight.budget / flight.cpm) * 1000)
                              : null;
                          return {
                            key: idx,
                            ...flight,
                            numberOfDays,
                            impressionGoal,
                          };
                        })}
                        columns={[
                          {
                            title: (
                              <span>
                                Start Date <Text type="danger">*</Text>
                              </span>
                            ),
                            dataIndex: 'startDate',
                            key: 'startDate',
                            width: 150,
                            render: (value, _, idx) => (
                              <DatePicker
                                style={{
                                  width: '100%',
                                  borderColor: !value ? '#ff4d4f' : undefined,
                                }}
                                value={value ? moment(value).tz(timezone) : null}
                                format="MM/DD/YYYY"
                                placeholder="Start date"
                                status={!value ? 'error' : undefined}
                                onChange={(date) => {
                                  const updated = [...flightSchedule];
                                  const normalizedDate = date
                                    ? moment
                                        .tz(date.format('YYYY-MM-DD'), timezone)
                                        .startOf('day')
                                        .utc()
                                    : null;
                                  updated[idx] = { ...updated[idx], startDate: normalizedDate };
                                  setFlightSchedule(updated);
                                }}
                              />
                            ),
                          },
                          {
                            title: (
                              <span>
                                End Date <Text type="danger">*</Text>
                              </span>
                            ),
                            dataIndex: 'endDate',
                            key: 'endDate',
                            width: 150,
                            render: (value, _, idx) => (
                              <DatePicker
                                style={{
                                  width: '100%',
                                  borderColor: !value ? '#ff4d4f' : undefined,
                                }}
                                value={value ? moment(value).tz(timezone) : null}
                                format="MM/DD/YYYY"
                                placeholder="End date"
                                status={!value ? 'error' : undefined}
                                onChange={(date) => {
                                  const updated = [...flightSchedule];
                                  const normalizedDate = date
                                    ? moment
                                        .tz(date.format('YYYY-MM-DD'), timezone)
                                        .endOf('day')
                                        .utc()
                                    : null;
                                  updated[idx] = { ...updated[idx], endDate: normalizedDate };
                                  setFlightSchedule(updated);
                                  autoAdjustEndDatesFromFlightSchedule(updated);
                                }}
                              />
                            ),
                          },
                          {
                            title: (
                              <span>
                                Budget $ <Text type="danger">*</Text>
                              </span>
                            ),
                            dataIndex: 'budget',
                            key: 'budget',
                            width: 150,
                            render: (value, _, idx) => (
                              <InputNumber
                                value={value}
                                onChange={(newValue) => {
                                  const updated = [...flightSchedule];
                                  const newPercentage =
                                    campaignBudget > 0
                                      ? parseFloat(((newValue / campaignBudget) * 100).toFixed(2))
                                      : 0;
                                  updated[idx] = {
                                    ...updated[idx],
                                    budget: newValue,
                                    budgetPercentage: newPercentage,
                                  };
                                  setFlightSchedule(updated);
                                }}
                                addonBefore="$"
                                min={0}
                                step={0.01}
                                style={{ width: '100%' }}
                                status={!value || value <= 0 ? 'error' : undefined}
                              />
                            ),
                          },
                          {
                            title: 'Budget %',
                            dataIndex: 'budgetPercentage',
                            key: 'budgetPercentage',
                            width: 150,
                            render: (_, record, idx) => {
                              const percentageValue =
                                record.budgetPercentage != null && record.budgetPercentage !== ''
                                  ? record.budgetPercentage
                                  : campaignBudget > 0
                                    ? parseFloat(
                                        ((record.budget / campaignBudget) * 100).toFixed(2)
                                      )
                                    : 0;

                              return (
                                <InputNumber
                                  value={percentageValue}
                                  onChange={(newPercentage) => {
                                    const newBudget =
                                      campaignBudget > 0
                                        ? parseFloat(
                                            ((newPercentage / 100) * campaignBudget).toFixed(2)
                                          )
                                        : 0;
                                    const updated = [...flightSchedule];
                                    updated[idx] = {
                                      ...updated[idx],
                                      budget: newBudget,
                                      budgetPercentage: newPercentage,
                                    };
                                    setFlightSchedule(updated);
                                  }}
                                  addonAfter="%"
                                  min={0}
                                  max={100}
                                  step={0.01}
                                  precision={2}
                                  style={{ width: '100%' }}
                                />
                              );
                            },
                          },
                          // CPM column - hidden for Percentage pricing
                          ...(!hideCpmFields
                            ? [
                                {
                                  title: (
                                    <span>
                                      CPM <Text type="danger">*</Text>
                                    </span>
                                  ),
                                  dataIndex: 'cpm',
                                  key: 'cpm',
                                  width: 120,
                                  render: (value, _, idx) => (
                                    <InputNumber
                                      style={{
                                        width: '100%',
                                        borderColor: !value || value < 1 ? '#ff4d4f' : undefined,
                                      }}
                                      value={value}
                                      placeholder="10"
                                      min={0}
                                      precision={2}
                                      status={!value || value < 1 ? 'error' : undefined}
                                      onChange={(newValue) => {
                                        const updated = [...flightSchedule];
                                        updated[idx] = { ...updated[idx], cpm: newValue };
                                        setFlightSchedule(updated);
                                      }}
                                    />
                                  ),
                                },
                              ]
                            : []),
                          // Impressions column - hidden for Percentage pricing
                          ...(!hideCpmFields
                            ? [
                                {
                                  title: 'Impressions',
                                  dataIndex: 'impressionGoal',
                                  key: 'impressionGoal',
                                  width: 120,
                                  align: 'right',
                                  render: (value) => (
                                    <Text>{value ? value.toLocaleString() : '-'}</Text>
                                  ),
                                },
                              ]
                            : []),
                          {
                            title: 'Days',
                            dataIndex: 'numberOfDays',
                            key: 'numberOfDays',
                            width: 80,
                            align: 'center',
                            render: (value) => <Text>{value !== null ? value : '-'}</Text>,
                          },
                          {
                            title: '',
                            key: 'actions',
                            width: 50,
                            align: 'center',
                            render: (_, __, idx) => (
                              <Button
                                danger
                                size="small"
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  const updated = flightSchedule.filter((_, i) => i !== idx);
                                  setFlightSchedule(updated);
                                }}
                              />
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        locale={{
                          emptyText:
                            'No campaign flight dates configured. Click "Add Flight" to add a new flight.',
                        }}
                        summary={(pageData) => {
                          if (pageData.length === 0) return null;

                          let totalBudget = 0;
                          let totalImpressions = 0;

                          pageData.forEach((record) => {
                            totalBudget += parseFloat(record.budget) || 0;
                            totalImpressions += record.impressionGoal || 0;
                          });

                          const totalPercentage =
                            campaignBudget > 0
                              ? parseFloat(((totalBudget / campaignBudget) * 100).toFixed(2))
                              : 0;

                          // Use campaign budget when percentage is 100% to avoid floating-point display issues
                          const displayBudget =
                            totalPercentage === 100 ? campaignBudget : totalBudget;

                          return (
                            <Table.Summary fixed>
                              <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>
                                  <Text strong>Total</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} />
                                <Table.Summary.Cell index={2}>
                                  <Text strong>
                                    $
                                    {displayBudget.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>
                                  <Text strong>{totalPercentage}%</Text>
                                </Table.Summary.Cell>
                                {!hideCpmFields && <Table.Summary.Cell index={4} />}
                                {!hideCpmFields && (
                                  <Table.Summary.Cell index={5} align="right">
                                    <Text strong>{totalImpressions.toLocaleString()}</Text>
                                  </Table.Summary.Cell>
                                )}
                                <Table.Summary.Cell index={hideCpmFields ? 4 : 6} />
                                <Table.Summary.Cell index={hideCpmFields ? 5 : 7} />
                              </Table.Summary.Row>
                            </Table.Summary>
                          );
                        }}
                      />
                    </Space>
                  </Card>
                </Col>
              )}

            {/* Bulk Line Item Management Section - Deal IDs and Creatives (Tabbed) */}
            {isAdvancedMode && isNotDraftStatus && hasTraffickerOrProcessorPermission && (
              <Col span={24}>
                <Divider orientation="left" style={{ marginTop: 24, marginBottom: 8 }}>
                  <Text strong>Bulk Line Item Management</Text>
                </Divider>

                <Tabs
                  defaultActiveKey="targetingType"
                  items={[
                    // Tab 1: Deal IDs
                    {
                      key: 'dealIds',
                      label: (
                        <span>
                          <TagsOutlined /> Deal IDs
                          {campaignDsp !== 'NEXXEN' && (
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                              (NEXXEN only)
                            </Text>
                          )}
                        </span>
                      ),
                      children: (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {campaignDsp !== 'NEXXEN' && (
                            <Alert
                              type="info"
                              message="Select NEXXEN as the Demand-Side Platform to enable deal IDs"
                              showIcon
                              style={{ marginBottom: 8 }}
                            />
                          )}
                          {loadingDealIds && dealsFetchProgress && (
                            <Space>
                              <Spin size="small" />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {dealsFetchProgress}
                              </Text>
                            </Space>
                          )}
                          <Input.TextArea
                            placeholder={
                              campaignDsp !== 'NEXXEN'
                                ? 'Select NEXXEN as the Demand-Side Platform to enable deal IDs'
                                : loadingDealIds
                                  ? 'Fetching publisher deals from Nexxen...'
                                  : 'Enter deal IDs, one per line or comma-separated'
                            }
                            rows={4}
                            style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
                            value={campaignDealIds}
                            onChange={(e) => setCampaignDealIds(e.target.value)}
                            onBlur={(e) => {
                              const normalized = e.target.value
                                .split(/[,\n]+/)
                                .map((s) => s.trim())
                                .filter(Boolean)
                                .join('\n');
                              setCampaignDealIds(normalized);
                            }}
                            disabled={loadingDealIds || campaignDsp !== 'NEXXEN'}
                          />
                          <Space wrap>
                            <Button
                              type="primary"
                              onClick={handleApplyDealIdsToLineItems}
                              disabled={
                                loadingDealIds ||
                                campaignDsp !== 'NEXXEN' ||
                                !campaignDealIds?.trim() ||
                                !lineItems?.length
                              }
                            >
                              Apply to Line Items
                            </Button>
                            <Button
                              icon={<ClearOutlined />}
                              onClick={handleClearAllDealIds}
                              disabled={
                                loadingDealIds || campaignDsp !== 'NEXXEN' || !lineItems?.length
                              }
                            >
                              Clear All Deal IDs
                            </Button>
                            {campaignDsp === 'NEXXEN' &&
                              !loadingDealIds &&
                              lineItems?.length > 0 && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Will apply to {lineItems.length} line item(s)
                                </Text>
                              )}
                            {campaignDsp === 'NEXXEN' &&
                              !loadingDealIds &&
                              publisherDeals.length > 0 && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  ({publisherDeals.length} deals available)
                                </Text>
                              )}
                          </Space>
                          {/* Warning for malformed deal IDs */}
                          {campaignDsp === 'NEXXEN' &&
                            !loadingDealIds &&
                            malformedDealIds.length > 0 && (
                              <Alert
                                type="error"
                                showIcon
                                message={`${malformedDealIds.length} deal ID(s) have invalid format`}
                                description={
                                  <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      Deal IDs must contain only letters, numbers, dashes, and
                                      underscores (max 100 characters):
                                    </Text>
                                    <div style={{ marginTop: 8 }}>
                                      {malformedDealIds.slice(0, 10).map((dealId, idx) => (
                                        <Tag key={idx} color="red" style={{ marginBottom: 4 }}>
                                          {dealId.length > 30
                                            ? `${dealId.substring(0, 30)}...`
                                            : dealId}
                                        </Tag>
                                      ))}
                                      {malformedDealIds.length > 10 && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          ... and {malformedDealIds.length - 10} more
                                        </Text>
                                      )}
                                    </div>
                                  </div>
                                }
                              />
                            )}
                          {/* Warning for invalid deal IDs (not found in Nexxen) */}
                          {campaignDsp === 'NEXXEN' &&
                            !loadingDealIds &&
                            invalidDealIds.length > 0 && (
                              <Alert
                                type="warning"
                                showIcon
                                message={`${invalidDealIds.length} deal ID(s) not found in Nexxen`}
                                description={
                                  <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      The following deal IDs do not exist in the available publisher
                                      deals and will not be applied:
                                    </Text>
                                    <div style={{ marginTop: 8 }}>
                                      {invalidDealIds.slice(0, 10).map((dealId, idx) => (
                                        <Tag key={idx} color="orange" style={{ marginBottom: 4 }}>
                                          {dealId}
                                        </Tag>
                                      ))}
                                      {invalidDealIds.length > 10 && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          ... and {invalidDealIds.length - 10} more
                                        </Text>
                                      )}
                                    </div>
                                  </div>
                                }
                              />
                            )}
                        </Space>
                      ),
                    },
                    // Tab 2: Targeting Type
                    {
                      key: 'targetingType',
                      label: (
                        <span>
                          <AimOutlined /> Targeting Type
                          {bulkTargetingType && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              {bulkTargetingType === 'COUNTRY'
                                ? 'Country'
                                : bulkTargetingType === 'REGION'
                                  ? 'Region'
                                  : 'Local'}
                            </Tag>
                          )}
                        </span>
                      ),
                      children: (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text type="secondary">
                            Select a targeting type and configure targeting to apply to multiple
                            line items:
                          </Text>

                          <Select
                            placeholder="Select targeting type"
                            style={{ width: 300 }}
                            value={bulkTargetingType}
                            onChange={(value) => {
                              setBulkTargetingType(value);
                              // Clear targeting data when type changes
                              setBulkTargetingCountry(null);
                              setSelectedBulkRegionGroups([]);
                              setSelectedBulkTargetingGroups([]);
                            }}
                            allowClear
                            options={[
                              { value: 'REGION', label: 'Target specific regions' },
                              { value: 'LOCAL', label: 'Local Targeting' },
                              { value: 'COUNTRY', label: 'Target an entire country' },
                            ]}
                          />

                          {bulkTargetingType && (
                            <div
                              style={{
                                marginTop: 8,
                                padding: 16,
                                border: '1px solid #d9d9d9',
                                borderRadius: 8,
                                backgroundColor: '#fafafa',
                              }}
                            >
                              {/* Country Selector */}
                              {bulkTargetingType === 'COUNTRY' && (
                                <div style={{ marginBottom: 16 }}>
                                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    Select Country:
                                  </Text>
                                  <Select
                                    placeholder="Select a country"
                                    style={{ width: 300 }}
                                    value={bulkTargetingCountry}
                                    onChange={setBulkTargetingCountry}
                                    allowClear
                                    options={countries.map((country) => ({
                                      label: country.name,
                                      value: country.code,
                                    }))}
                                  />
                                </div>
                              )}

                              {/* Region Groups Selector */}
                              {bulkTargetingType === 'REGION' && (
                                <div style={{ marginBottom: 16 }}>
                                  <Space style={{ marginBottom: 8 }}>
                                    <Text strong>Region Groups:</Text>
                                    <Button
                                      type={
                                        selectedBulkRegionGroups.length > 0 ? 'default' : 'primary'
                                      }
                                      size="small"
                                      onClick={() => setShowBulkRegionGroupsModal(true)}
                                    >
                                      {selectedBulkRegionGroups.length > 0
                                        ? 'Change Region Groups'
                                        : 'Select Region Groups'}
                                    </Button>
                                  </Space>
                                  {selectedBulkRegionGroups.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                      {selectedBulkRegionGroups.map((region, idx) => (
                                        <Tag
                                          key={idx}
                                          color="blue"
                                          closable
                                          onClose={() =>
                                            setSelectedBulkRegionGroups((prev) =>
                                              prev.filter((_, i) => i !== idx)
                                            )
                                          }
                                          style={{ marginBottom: 4 }}
                                        >
                                          {region.name}
                                        </Tag>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Local Targeting Groups Selector */}
                              {bulkTargetingType === 'LOCAL' && (
                                <div style={{ marginBottom: 16 }}>
                                  <Space style={{ marginBottom: 8 }}>
                                    <Text strong>Targeting Groups:</Text>
                                    <Button
                                      type={
                                        selectedBulkTargetingGroups.length > 0
                                          ? 'default'
                                          : 'primary'
                                      }
                                      size="small"
                                      onClick={() => setShowBulkTargetingGroupsModal(true)}
                                    >
                                      {selectedBulkTargetingGroups.length > 0
                                        ? 'Change Targeting Groups'
                                        : 'Select Targeting Groups'}
                                    </Button>
                                  </Space>
                                  {selectedBulkTargetingGroups.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                      {selectedBulkTargetingGroups.map((targeting, idx) => (
                                        <Tag
                                          key={idx}
                                          color="green"
                                          closable
                                          onClose={() =>
                                            setSelectedBulkTargetingGroups((prev) =>
                                              prev.filter((_, i) => i !== idx)
                                            )
                                          }
                                          style={{ marginBottom: 4 }}
                                        >
                                          {targeting.name}
                                        </Tag>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              <Divider style={{ margin: '12px 0' }} />

                              <Space style={{ marginBottom: 8 }}>
                                <Text strong>Apply to Line Items:</Text>
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() =>
                                    setTargetingLineItemIndices(lineItems.map((_, idx) => idx))
                                  }
                                >
                                  Select All
                                </Button>
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() => setTargetingLineItemIndices([])}
                                >
                                  Clear All
                                </Button>
                              </Space>
                              <Checkbox.Group
                                value={targetingLineItemIndices}
                                onChange={setTargetingLineItemIndices}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 4,
                                  marginBottom: 16,
                                }}
                              >
                                {lineItems.map((li, idx) => (
                                  <Checkbox key={idx} value={idx}>
                                    {li.name || `Line Item ${idx + 1}`}
                                    {li.targetingType && (
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: 11, marginLeft: 8 }}
                                      >
                                        (current: {li.targetingType})
                                      </Text>
                                    )}
                                  </Checkbox>
                                ))}
                              </Checkbox.Group>

                              <Space>
                                <Button
                                  type="primary"
                                  onClick={handleApplyTargetingType}
                                  disabled={
                                    targetingLineItemIndices.length === 0 ||
                                    (bulkTargetingType === 'COUNTRY' && !bulkTargetingCountry) ||
                                    (bulkTargetingType === 'REGION' &&
                                      selectedBulkRegionGroups.length === 0) ||
                                    (bulkTargetingType === 'LOCAL' &&
                                      selectedBulkTargetingGroups.length === 0)
                                  }
                                >
                                  Apply to Line Items
                                </Button>
                                <Button
                                  onClick={() => {
                                    setBulkTargetingType(null);
                                    setTargetingLineItemIndices([]);
                                    setBulkTargetingCountry(null);
                                    setSelectedBulkRegionGroups([]);
                                    setSelectedBulkTargetingGroups([]);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Will apply to {targetingLineItemIndices.length} line item(s)
                                </Text>
                              </Space>
                            </div>
                          )}
                        </Space>
                      ),
                    },
                    // Tab 3: Audience
                    {
                      key: 'audience',
                      label: (
                        <span>
                          <TeamOutlined /> Audience
                        </span>
                      ),
                      children: (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text type="secondary">
                            Select an audience type to apply to multiple line items:
                          </Text>

                          <Select
                            placeholder="Select audience type"
                            style={{ width: 300 }}
                            value={bulkAudienceType}
                            onChange={setBulkAudienceType}
                            allowClear
                            options={[
                              { value: 'FIRST_PARTY_DATA', label: 'First Party Data' },
                              { value: 'ALL_USERS', label: 'All users in targeting area aged 21+' },
                            ]}
                          />

                          {bulkAudienceType && (
                            <div
                              style={{
                                marginTop: 8,
                                padding: 16,
                                border: '1px solid #d9d9d9',
                                borderRadius: 8,
                                backgroundColor: '#fafafa',
                              }}
                            >
                              <Space style={{ marginBottom: 8 }}>
                                <Text strong>Apply to Line Items:</Text>
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() =>
                                    setAudienceLineItemIndices(lineItems.map((_, idx) => idx))
                                  }
                                >
                                  Select All
                                </Button>
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() => setAudienceLineItemIndices([])}
                                >
                                  Clear All
                                </Button>
                              </Space>
                              <Checkbox.Group
                                value={audienceLineItemIndices}
                                onChange={setAudienceLineItemIndices}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 4,
                                  marginBottom: 16,
                                }}
                              >
                                {lineItems.map((li, idx) => (
                                  <Checkbox key={idx} value={idx}>
                                    {li.name || `Line Item ${idx + 1}`}
                                    {li.audienceType && (
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: 11, marginLeft: 8 }}
                                      >
                                        (current:{' '}
                                        {li.audienceType === 'FIRST_PARTY_DATA'
                                          ? 'First Party'
                                          : 'All Users'}
                                        )
                                      </Text>
                                    )}
                                  </Checkbox>
                                ))}
                              </Checkbox.Group>

                              <Space>
                                <Button
                                  type="primary"
                                  onClick={handleApplyAudienceType}
                                  disabled={audienceLineItemIndices.length === 0}
                                >
                                  Apply to Line Items
                                </Button>
                                <Button
                                  onClick={() => {
                                    setBulkAudienceType(null);
                                    setAudienceLineItemIndices([]);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Will apply to {audienceLineItemIndices.length} line item(s)
                                </Text>
                              </Space>
                            </div>
                          )}
                        </Space>
                      ),
                    },
                    // Tab 4: Ad Group
                    {
                      key: 'adGroup',
                      label: (
                        <span>
                          <PictureOutlined /> Ad Group
                          {selectedAdGroups.length > 0 && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              {selectedAdGroups.length} selected
                            </Tag>
                          )}
                        </span>
                      ),
                      children: (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {/* Select Ad Groups Button */}
                          <Space>
                            <Button
                              type={selectedAdGroups.length > 0 ? 'default' : 'primary'}
                              icon={<PictureOutlined />}
                              onClick={() => setShowCampaignAdGroupsModal(true)}
                              disabled={!lineItems?.length}
                            >
                              {selectedAdGroups.length > 0
                                ? 'Change Ad Groups'
                                : 'Select Ad Groups'}
                            </Button>
                            <Button
                              icon={<ClearOutlined />}
                              onClick={handleClearAllCreatives}
                              disabled={
                                !lineItems?.length ||
                                !lineItems.some((li) => li.adGroup?.adGroupCreatives?.length)
                              }
                            >
                              Clear All Ad Groups
                            </Button>
                          </Space>

                          {/* Selected Ad Groups Preview */}
                          {selectedAdGroups.length > 0 && (
                            <div
                              style={{
                                marginTop: 8,
                                padding: 16,
                                border: '1px solid #d9d9d9',
                                borderRadius: 8,
                                backgroundColor: '#fafafa',
                              }}
                            >
                              <Text strong style={{ display: 'block', marginBottom: 12 }}>
                                Selected Ad Groups ({selectedAdGroups.length})
                              </Text>

                              {/* Ad Groups Table with Click-through URLs */}
                              <Table
                                size="small"
                                pagination={false}
                                dataSource={selectedAdGroups.map((ag, idx) => ({
                                  ...ag,
                                  key: ag.id || `${ag.name}-${idx}`,
                                }))}
                                columns={[
                                  {
                                    title: 'Ad Group Name',
                                    dataIndex: 'name',
                                    key: 'name',
                                    width: 200,
                                    ellipsis: true,
                                  },
                                  {
                                    title: 'Ad Units',
                                    dataIndex: 'medias',
                                    key: 'medias',
                                    width: 80,
                                    render: (medias) => medias?.length || 0,
                                  },
                                  {
                                    title: 'Click-Through URL',
                                    key: 'clickthrough',
                                    render: (_, record) => {
                                      const url = adGroupClickthroughs[record.name] || '';
                                      const hasError = url && !isValidUrl(url);
                                      return (
                                        <Tooltip
                                          title={hasError ? 'Please enter a valid URL' : ''}
                                          open={hasError ? undefined : false}
                                        >
                                          <Input
                                            placeholder="https://example.com"
                                            value={url}
                                            onChange={(e) =>
                                              setAdGroupClickthroughs((prev) => ({
                                                ...prev,
                                                [record.name]: e.target.value,
                                              }))
                                            }
                                            style={{ width: '100%' }}
                                            status={hasError ? 'error' : undefined}
                                          />
                                        </Tooltip>
                                      );
                                    },
                                  },
                                  {
                                    title: '',
                                    key: 'actions',
                                    width: 50,
                                    render: (_, record) => (
                                      <Button
                                        type="text"
                                        icon={<DeleteOutlined />}
                                        danger
                                        size="small"
                                        onClick={() => handleRemoveSelectedAdGroup(record.name)}
                                      />
                                    ),
                                  },
                                ]}
                                style={{ marginBottom: 16 }}
                              />

                              {/* Line Item Selection */}
                              <div style={{ marginBottom: 16 }}>
                                <Space style={{ marginBottom: 8 }}>
                                  <Text strong>Apply to Line Items:</Text>
                                  <Button
                                    size="small"
                                    type="link"
                                    onClick={() =>
                                      setSelectedLineItemIndices(lineItems.map((_, idx) => idx))
                                    }
                                  >
                                    Select All
                                  </Button>
                                  <Button
                                    size="small"
                                    type="link"
                                    onClick={() => setSelectedLineItemIndices([])}
                                  >
                                    Clear All
                                  </Button>
                                </Space>
                                <Checkbox.Group
                                  value={selectedLineItemIndices}
                                  onChange={setSelectedLineItemIndices}
                                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                                >
                                  {lineItems.map((li, idx) => (
                                    <Checkbox key={idx} value={idx}>
                                      {li.name || `Line Item ${idx + 1}`}
                                      {li.adGroup?.adGroupCreatives?.length > 0 && (
                                        <Text
                                          type="secondary"
                                          style={{ fontSize: 11, marginLeft: 8 }}
                                        >
                                          (has {li.adGroup.adGroupCreatives.length} creative
                                          {li.adGroup.adGroupCreatives.length > 1 ? 's' : ''})
                                        </Text>
                                      )}
                                    </Checkbox>
                                  ))}
                                </Checkbox.Group>
                              </div>

                              {/* Apply and Clear Buttons */}
                              <Space>
                                <Button
                                  type="primary"
                                  onClick={handleApplyAdGroupsToLineItems}
                                  disabled={selectedLineItemIndices.length === 0}
                                >
                                  Apply to Line Items
                                </Button>
                                <Button onClick={handleClearSelectedAdGroups}>Cancel</Button>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Will apply {selectedAdGroups.length} ad group(s) to{' '}
                                  {selectedLineItemIndices.length} line item(s)
                                </Text>
                              </Space>
                            </div>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                />
              </Col>
            )}

            {/* Validation Alerts */}
            {!dateValidation.valid && dateValidation.error && (
              <Col span={24}>
                <Alert
                  type="error"
                  message="Date Range Error"
                  description={dateValidation.error}
                  showIcon
                  style={{ marginBottom: 0 }}
                />
              </Col>
            )}

            {!budgetValidation.valid && budgetValidation.error && (
              <Col span={24}>
                <Alert
                  type="error"
                  message="Budget Allocation Error"
                  description={budgetValidation.error}
                  showIcon
                  style={{ marginBottom: 0 }}
                />
              </Col>
            )}
          </Row>
        </Card>

        {/* Campaign Ad Groups Dialog */}
        {showCampaignAdGroupsModal && (
          <CampaignRequestorAdGroupsDialog
            visible={showCampaignAdGroupsModal}
            setVisible={setShowCampaignAdGroupsModal}
            orgId={form?.getFieldValue('advertiserId')}
            selectedAdGroupCreatives={selectedAdGroups}
            onAddSelections={handleAdGroupsSelected}
          />
        )}

        {/* Bulk Region Groups Dialog */}
        {showBulkRegionGroupsModal && (
          <CampaignRequestorRegionGroupsDialog
            visible={showBulkRegionGroupsModal}
            setVisible={setShowBulkRegionGroupsModal}
            selectedRegionGroups={selectedBulkRegionGroups}
            onAddSelections={setSelectedBulkRegionGroups}
          />
        )}

        {/* Bulk Targeting Groups Dialog */}
        {showBulkTargetingGroupsModal && (
          <CampaignRequestorTargetingGroupsDialog
            visible={showBulkTargetingGroupsModal}
            setVisible={setShowBulkTargetingGroupsModal}
            selectedTargetingGroups={selectedBulkTargetingGroups}
            onAddSelections={setSelectedBulkTargetingGroups}
          />
        )}
      </>
    );
  }
);

CampaignHeader.displayName = 'CampaignHeader';

export default CampaignHeader;
